import React, { Component } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  company,
  loading,
  NavbarWarehouse,
  alert,
  GET,
  POST,
  t,
} from "../../components/CustomComponent.js";
import { can } from "../../utils/permission";
import { matchColumnsByHeader } from "../../utils/headerMatch";
// หน้าตรวจข้อมูลก่อนนำเข้า ใช้ตัวเดียวกับปุ่มนำเข้าในเมนูสต็อกสินค้า
import ImportPreviewModal, { importErrorText, stockImportSummaryText } from "../../components/ImportPreviewModal.js";

import locationTemplateFile from "../../assets/template/12_Location_Template.xlsx";
import warehouseTemplateFile from "../../assets/template/11_Warehouse_Template.xlsx";
import uomTemplateFile from "../../assets/template/07_UnitOfMeasure_Template.xlsx";
import categoryTemplateFile from "../../assets/template/08_Category_Template.xlsx";
import brandTemplateFile from "../../assets/template/05.2_Brand_Template.xlsx";
import modelTemplateFile from "../../assets/template/03_Model_Template.xlsx";
import partTemplateFile from "../../assets/template/09_Item_Master_Template.xlsx";
import customerTemplateFile from "../../assets/template/01_Customer_Template.xlsx";
import wbsTemplateFile from "../../assets/template/13_wbs_Template.xlsx";
import projectTemplateFile from "../../assets/template/16_project_Template.xlsx";
import inventoryTemplateFile from "../../assets/template/14_Inventory_Template.xlsx";
import technicianTemplateFile from "../../assets/template/15_Technician_Template.xlsx";
import assetTemplateFile from "../../assets/template/10_Asset_Template.xlsx";
import siteTemplateFile from "../../assets/template/17_Site_Template.xlsx";
import sectionTemplateFile from "../../assets/template/18_Department_Template.xlsx";
import machineTemplateFile from "../../assets/template/19_Machine_Equipment.xlsx";

// สต็อกใช้ตัวอ่านไฟล์/ตัวนำเข้าชุดเดียวกับหน้า Stock (นำเข้าแบบ full-replace ผ่าน batch)
import {
  STOCK_IMPORT_COLUMNS,
  STOCK_IMPORT_SHEET,
  STOCK_IMPORT_HEADER_ROWS,
  parseStockWorkbook,
  runStockImport,
} from "../../utils/stockImport.js";

/**
 * Column definitions for the Location data set.
 * `type`:
 *   auto   -> running number generated by the system (not validated / not imported)
 *   text   -> free text
 *   enum   -> must match one of `options` (matched by english key or thai label)
 *   number -> decimal number (optional)
 *   status -> เปิดใช้งาน / ปิดใช้งาน  ->  boolean
 */
const LOCATION_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },
  { key: "location_code", th: "รหัสตำแหน่ง", en: "Location Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อตำแหน่ง", en: "Location Name", type: "text", required: true },
  { key: "location_type", th: "ประเภทตำแหน่ง", en: "Location Type", type: "enum", required: true, options: ["zone", "row", "rack", "room", "other"] },
  { key: "address", th: "ที่อยู่ / รายละเอียด", en: "Address / Detail", type: "text" },
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number" },
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Warehouse data set (11_Warehouse_Template.xlsx / Template No. 08).
 * Sheet "Data Template" — headers on row 8, data starts at row 9 (A..H).
 * `department_code` (Site Code, column B) is a Lookup against the company's sites (หน่วยงาน).
 * `warehouse_type` accepts the template's labels (คลังหลัก / คลังย่อย) and maps them to main / sub.
 * `storage_locations` is a comma-separated list of Location codes; one location belongs to one warehouse only.
 * Import order: Site -> Location -> Warehouse.
 */
const WAREHOUSE_TYPE_LABELS = { main: "คลังหลัก", sub: "คลังย่อย" };
const WAREHOUSE_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },                                                           // A
  { key: "department_code", th: "รหัสหน่วยงาน", en: "Site Code", type: "text", required: true },                    // B (lookup)
  { key: "warehouse_code", th: "รหัสคลังสินค้า", en: "Warehouse Code", type: "text", required: true, unique: true }, // C
  { key: "name", th: "ชื่อคลังสินค้า", en: "Warehouse Name", type: "text", required: true },                        // D
  {
    key: "warehouse_type", th: "ประเภทคลัง", en: "Warehouse Type", type: "enum", required: true,                    // E
    enumAliases: { main: [WAREHOUSE_TYPE_LABELS.main, "Main", "หลัก"], sub: [WAREHOUSE_TYPE_LABELS.sub, "Sub", "ย่อย"] },
  },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },                                          // F
  { key: "storage_locations", th: "พื้นที่เก็บ", en: "Storage Locations", type: "list" },                           // G
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                                  // H
];

/**
 * Column definitions for the Unit of Measure data set (Template No. 07).
 * No lookup/enum columns — status accepts Active/Inactive (เปิดใช้งาน/ปิดใช้งาน).
 */
const UOM_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "uom_code", th: "รหัสหน่วยนับ", en: "Unit Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหน่วยนับ", en: "Unit Name", type: "text", required: true },
  { key: "symbol", th: "สัญลักษณ์", en: "Symbol", type: "text" },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the Category data set (Template No. 08).
 * Column headers are on row 8 (row 7 holds group titles) -> data starts at row 9.
 * Part Control columns (E-H) are Yes/No flags mapped into a `field_control` array.
 */
const CATEGORY_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },
  { key: "category_code", th: "รหัสหมวดหมู่", en: "Category Code", type: "text", required: true, unique: true },
  { key: "name", th: "ชื่อหมวดหมู่", en: "Category Name", type: "text", required: true },
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },
  { key: "warehouse_control", th: "Warehouse", en: "Warehouse Control", type: "bool" },
  { key: "location_control", th: "Location", en: "Location Control", type: "bool" },
  { key: "batch_control", th: "Batch Number", en: "Batch Number Control", type: "bool" },
  { key: "date_control", th: "Date", en: "Date Control", type: "bool" },
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },
];

/**
 * Column definitions for the แบรนด์ (Brand) data set (Template No. 05.2 — 05.2_Brand_Template.xlsx) — table `brands`.
 * Sheet "Brand Template" — header แถว 7, ข้อมูลเริ่มแถว 8: A ลำดับที่ · B รหัสแบรนด์ · C ชื่อแบรนด์ · D คำอธิบาย · E สถานะ
 * payload keys ยังเป็น brand_* ตาม API เดิม. รหัส/ชื่อถูก trim + ตัวพิมพ์ใหญ่ก่อนส่ง
 * รหัสซ้ำกับที่มีในระบบ -> update แทน (backend upsert ด้วย brand_code เทียบแบบไม่สนตัวพิมพ์, ไม่แตะ parent/level)
 * ตัวตรวจ header เทียบกับไฟล์ template ที่ bundle มา (ไม่ได้เทียบกับ th/en ในรายการนี้)
 */
const BRAND_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                              // A
  { key: "brand_code", th: "รหัสแบรนด์", en: "Brand Code", type: "text", required: true, unique: true }, // B
  { key: "name", th: "ชื่อแบรนด์", en: "Brand Name", type: "text", required: true },                     // C
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },                                 // D
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                         // E
];

// รหัส/ชื่อ: trim + ตัวพิมพ์ใหญ่ (กฎเดียวกับหน้า แบรนด์ และ backend)
const normalizeBrandText = (value) => String(value ?? "").trim().toUpperCase();

/**
 * Column definitions for the รุ่น (Model) data set (Template No. 03 — 03_Model_Template.xlsx) — table `models`.
 * Sheet "Model Template" — header แถว 7, ข้อมูลเริ่มแถว 8:
 *   A ลำดับที่ (auto) · B รหัสรุ่น * · C ชื่อรุ่น * · D รหัสแบรนด์ · E ชื่อแบรนด์ · F คำอธิบาย · G สถานะ *
 * payload keys ยังเป็น model_* / brand_* ตาม API เดิม.
 * - กฎซ้ำ = "แบรนด์ + รหัสรุ่น" (รหัสเดียวกันอยู่คนละแบรนด์ได้ เช่น ชุดปั๊ม EBARA / HASCON) เทียบแบบไม่สนตัวพิมพ์
 *   -> ตรวจที่ postValidate: ซ้ำในไฟล์ = duplicate, คู่ที่มีในระบบแล้ว = exists (backend ไม่ update ทับ -> error)
 * - รหัสแบรนด์ไม่บังคับ (บางรุ่นไม่มีแบรนด์) แต่ถ้ากรอกต้องมีในระบบ (lookup) -> ต้อง Import Brand ให้เสร็จก่อน
 * - `brand_name` (E) เป็นคอลัมน์ Auto ในไฟล์ -> ไม่ใช้ตอน import (เติมตอน export)
 * - รหัสแบรนด์ถูก trim + ตัวพิมพ์ใหญ่ก่อนส่ง (กฎเดียวกับหน้า Brand); รหัส/ชื่อรุ่นแค่ trim
 */
const MODEL_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                            // A
  { key: "model_code", th: "รหัสรุ่น", en: "Model Code", type: "text", required: true },               // B (ซ้ำได้ข้ามแบรนด์ — ดู postValidate)
  { key: "name", th: "ชื่อรุ่น", en: "Model Name", type: "text", required: true },                     // C
  { key: "brand_code", th: "รหัสแบรนด์", en: "Brand Code", type: "text" },                             // D (lookup -> brands, ว่างได้)
  { key: "brand_name", th: "ชื่อแบรนด์", en: "Brand Name (Auto)", type: "auto" },                      // E
  { key: "description", th: "คำอธิบาย", en: "Description", type: "text" },                             // F
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                     // G
];


/**
 * Column definitions for the Part / Item Master data set (Template No. 09).
 * Headers are on row 8 (row 7 = group titles) -> data starts at row 9.
 * Columns must stay in exact A..AA order so cell indices line up with the sheet.
 * Lookup codes (Material Type / Material Group / Brand / category / uom) are resolved to *_id + *_name on save.
 * NOTE ชื่อ key ยังเป็น brand_/model_/serie_ ตาม API เดิม — ดู glossary ใน components/global.js
 * The *_name columns are auto VLOOKUP (ignored on import); Z/AA are not imported.
 */
const PART_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },                                   // A
  { key: "part_number", th: "รหัสอะไหล่", en: "Part ID", type: "text", required: true }, // B (duplicate -> upsert: existing updates, new creates)
  { key: "barcode", th: "Barcode", en: "Barcode", type: "text" },                            // C
  { key: "name", th: "คำอธิบาย", en: "Description", type: "text", required: true },           // D
  { key: "brand_code", th: "รหัสแบรนด์", en: "Brand Code", type: "text" },                     // E (lookup -> brands = แบรนด์)
  { key: "brand_name", th: "ชื่อแบรนด์", en: "Brand Name", type: "auto" },                     // F
  { key: "model_code", th: "รหัสรุ่น", en: "Model Code", type: "text" },                     // G (lookup -> models = โมเดล)
  { key: "model_name", th: "ชื่อรุ่น", en: "Model Name", type: "auto" },                     // H
  { key: "serie_name", th: "ซีรีส์", en: "Series", type: "text" },                             // I (lookup -> series = ซีรีส์, by name)
  { key: "serie_name_auto", th: "ชื่อซีรีส์", en: "Series Name", type: "auto" },               // J
  { key: "category_code_1", th: "รหัสหมวดหมู่ 1", en: "Category ID 1", type: "text", required: true }, // K (lookup)
  { key: "category_name_1", th: "ชื่อหมวดหมู่ 1", en: "Category Name 1", type: "auto" },       // L
  { key: "category_code_2", th: "รหัสหมวดหมู่ 2", en: "Category ID 2", type: "text" },         // M (lookup)
  { key: "category_name_2", th: "ชื่อหมวดหมู่ 2", en: "Category Name 2", type: "auto" },       // N
  { key: "category_code_3", th: "รหัสหมวดหมู่ 3", en: "Category ID 3", type: "text" },         // O (lookup)
  { key: "category_name_3", th: "ชื่อหมวดหมู่ 3", en: "Category Name 3", type: "auto" },       // P
  { key: "uom_code", th: "รหัสหน่วยนับ", en: "Unit ID", type: "text", required: true },        // Q (lookup)
  { key: "uom_name", th: "ชื่อหน่วยนับ", en: "Unit Name", type: "auto" },                      // R
  { key: "uom_cost", th: "ต้นทุนต่อหน่วย", en: "Unit Cost", type: "number" },                  // S
  { key: "selling_price", th: "ราคาขาย", en: "Selling Price", type: "number" },               // T
  { key: "min_stock", th: "สต็อกขั้นต่ำ", en: "Min Stock", type: "number" },                   // U
  { key: "reorder_point", th: "จุดสั่งซื้อ", en: "Reorder Point", type: "number" },            // V
  { key: "reorder_qty", th: "ปริมาณสั่งซื้อ", en: "Reorder Qty", type: "number" },             // W
  { key: "lead_time_days", th: "ระยะเวลารอ (วัน)", en: "Lead Time (Days)", type: "number" },   // X
  { key: "supplier_name", th: "ผู้จำหน่ายหลัก", en: "Main Supplier", type: "text" },           // Y
  { key: "legacy_ref", th: "อ้างอิงอะไหล่เก่า", en: "Reference Old Part", type: "auto" },       // Z (not imported)
  { key: "compatible_series", th: "ซีรีส์ที่ใช้ร่วมกันได้", en: "Compatible Series", type: "auto" }, // AA (not imported)
];

/**
 * Column definitions for the Customer data set (Template No. 01).
 * Sheet "Customer Template" — headers on row 7, data starts at row 8 (A..AE, 31 columns).
 * Column order MUST match the template sheet exactly (cells are read by position);
 * the header row of the uploaded file is checked against the bundled template before parsing.
 * Enum columns (type/group/source/contact_prefix/payment_type/currency/sla_level) use
 * `enumKeyMode: "label"` so the value is stored verbatim and matched against the
 * "Reference" sheet (which lists value in column B -> reference.valueColumn = 1).
 * `sla_level` is sent as text and resolved to priority_id (work_requisition_priorities.name_en)
 * on the backend. Status maps to both customer_status (string) and is_active (boolean).
 * Customer fields that the template does not carry (branch_type / branch_code /
 * delivery_zone / sub_district / mobile_phone / payment_terms) are simply not sent —
 * the backend keeps whatever the record already has for them.
 */
const CUSTOMER_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                            // A
  { key: "customer_code", th: "รหัสลูกค้า", en: "Customer Code", type: "text", required: true, unique: true }, // B
  { key: "full_name_th", th: "ชื่อลูกค้า (ไทย)", en: "Customer Name TH", type: "text", required: true },  // C
  { key: "alias_name", th: "ชื่อย่อ", en: "Alias", type: "text" },                                        // D
  { key: "full_name_en", th: "ชื่อลูกค้า (อังกฤษ)", en: "Customer Name EN", type: "text" },              // E
  { key: "type", th: "ประเภทลูกค้า", en: "Customer Type", type: "enum", required: true, enumKeyMode: "label" }, // F
  { key: "group", th: "กลุ่มลูกค้า", en: "Customer Group", type: "enum", enumKeyMode: "label" },          // G
  { key: "tax_number", th: "เลขผู้เสียภาษี", en: "Tax ID", type: "text" },                                // H
  { key: "active_start_date", th: "วันที่เริ่มเป็นลูกค้า", en: "Start Date", type: "date" },              // I
  { key: "source", th: "แหล่งที่มา", en: "Source", type: "enum", enumKeyMode: "label" },                  // J
  { key: "address", th: "ที่อยู่", en: "Address", type: "text" },                                          // K
  { key: "province", th: "จังหวัด", en: "Province", type: "text" },                                        // L
  { key: "district", th: "อำเภอ/เขต", en: "District", type: "text" },                                     // M
  { key: "zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                               // N
  { key: "phone", th: "โทรศัพท์ 1", en: "Phone 1", type: "text" },                                         // O
  { key: "phone_2", th: "โทรศัพท์ 2", en: "Phone 2", type: "text" },                                      // P
  { key: "fax", th: "โทรสาร", en: "Fax", type: "text" },                                                  // Q
  { key: "email", th: "อีเมล", en: "Email", type: "email" },                                              // R
  { key: "website", th: "เว็บไซต์", en: "Website", type: "text" },                                        // S
  { key: "contact_prefix", th: "คำนำหน้า", en: "Title", type: "enum", enumKeyMode: "label" },             // T
  { key: "contact_first_name", th: "ชื่อผู้ติดต่อหลัก", en: "Primary Contact", type: "text" },             // U
  { key: "contact_last_name", th: "นามสกุล", en: "Last Name", type: "text" },                             // V
  { key: "contact_position", th: "ตำแหน่ง", en: "Position", type: "text" },                               // W
  { key: "contact_phone", th: "โทรศัพท์ผู้ติดต่อ", en: "Contact Phone", type: "text" },                    // X
  { key: "contact_email", th: "อีเมลผู้ติดต่อ", en: "Contact Email", type: "email" },                     // Y
  { key: "payment_type", th: "วิธีการชำระเงิน", en: "Payment Method", type: "enum", enumKeyMode: "label" }, // Z
  { key: "currency", th: "สกุลเงิน", en: "Currency", type: "enum", enumKeyMode: "label" },                // AA
  { key: "sla_level", th: "ระดับ SLA เริ่มต้น", en: "SLA Level", type: "enum", enumKeyMode: "label" },    // AB
  { key: "account_owner", th: "ผู้ดูแลบัญชี", en: "Account Owner", type: "text" },                        // AC
  { key: "remark", th: "หมายเหตุ", en: "Remarks", type: "text" },                                         // AD
  // Tri-state status (Active / Inactive / Hold) -> stored verbatim in customer_status.
  { key: "customer_status", th: "สถานะ", en: "Status", type: "customer_status" },                          // AE
];

/**
 * Column definitions for the WBS data set (Template No. 13).
 * Sheet "project" — a flat export from an external ERP system (SAP): headers on
 * row 1, data starts at row 2, no group titles / instructions / Reference sheet.
 * One row = one WBS element. `project_code` (Project ID) repeats across the rows
 * of the same project, so the backend (admin/wbs/import) upserts the project by
 * `project_code` first (create if missing, otherwise update) and then upserts the
 * WBS by `wbs_element`, linked back to that project. No duplicate check on either
 * column — a repeated key simply updates the existing record.
 */
const WBS_COLUMNS = [
  { key: "entity_guid", th: "รหัสอ้างอิง (Entity GUID)", en: "Entity GUID", type: "text" },
  { key: "project_code", th: "รหัสโครงการ", en: "Project ID", type: "text", required: true },
  { key: "name", th: "ชื่อโครงการ", en: "Name", type: "text", required: true },
  { key: "wbs_element", th: "รหัส WBS", en: "WBS Element", type: "text" },
  { key: "wbs_element_name", th: "ชื่อ WBS", en: "WBS Element Name", type: "text" },
];

/**
 * Column definitions for the Project data set (Template No. 16).
 * Sheet "SAPUI5 Export" — the project list exported from SAP: headers on row 1, data from
 * row 2, one row = one project. `project_code` is the upsert key (existing -> update,
 * missing -> create); it is deliberately NOT `unique` here so a code repeated inside the
 * file is not rejected — the later row simply overwrites the earlier one.
 *
 * Dates: SAP writes dd.mm.yyyy (and "dd.mm.yyyy, HH:MM:SS" for Created On). Depending on how
 * the file was saved the cell arrives as an Excel date serial or as that text — both are
 * accepted (see normDate / normDateTime) and sent to the backend in one canonical form.
 */
const PROJECT_COLUMNS = [
  { key: "project_code", th: "รหัสโครงการ", en: "Project", type: "text", required: true },          // A
  { key: "name", th: "ชื่อโครงการ", en: "Project Name", type: "text", required: true },             // B
  { key: "owner_code", th: "รหัสเจ้าของโครงการ", en: "Owner ID", type: "text" },                     // C
  { key: "owner_name", th: "เจ้าของโครงการ", en: "Owner", type: "text" },                           // D
  { key: "planned_start_date", th: "วันเริ่มตามแผน", en: "Planned Start", type: "date" },           // E
  { key: "planned_finish_date", th: "วันสิ้นสุดตามแผน", en: "Planned Finish", type: "date" },       // F
  { key: "actual_start_date", th: "วันเริ่มจริง", en: "Actual Start", type: "date" },               // G
  { key: "actual_finish_date", th: "วันสิ้นสุดจริง", en: "Actual Finish", type: "date" },           // H
  { key: "processing_status", th: "สถานะดำเนินการ", en: "Processing Status", type: "text" },        // I
  { key: "project_manager", th: "ผู้จัดการโครงการ", en: "Project Manager", type: "text" },          // J
  { key: "sap_created_by", th: "ผู้สร้าง (SAP)", en: "Created By", type: "text" },                  // K
  { key: "description", th: "รายละเอียด", en: "Description", type: "text" },                        // L
  { key: "sap_created_on", th: "วันที่สร้าง (UTC)", en: "Created On (UTC)", type: "datetime" },     // M
];

/**
 * Column definitions for the Site (หน่วยงาน) data set (Template No. 17) — table `departments`.
 * Sheet "Site Template" — headers on row 8, data starts at row 9 (A..K).
 * UI name = "หน่วยงาน / Site"; payload keys ยังเป็น department_* ตาม API เดิม.
 * Only the Site Code is mandatory and it is the upsert key (company-scoped, case-insensitive).
 * `site_group` is not listed on the Reference sheet -> validated against `options`
 * (must stay in sync with SITE_GROUPS on the backend).
 */
const SITE_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                                  // A
  { key: "department_code", th: "รหัสหน่วยงาน", en: "Site Code", type: "text", required: true, unique: true }, // B
  { key: "name", th: "ชื่อหน่วยงาน", en: "Site Name", type: "text" },                                          // C
  { key: "address", th: "ที่อยู่", en: "Address", type: "text" },                                               // D
  { key: "province", th: "จังหวัด", en: "Province", type: "text" },                                            // E
  { key: "district", th: "อำเภอ/เขต", en: "District", type: "text" },                                          // F
  { key: "sub_district", th: "ตำบล/แขวง", en: "Sub-district", type: "text" },                                  // G
  { key: "zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                                    // H
  { key: "site_group", th: "กลุ่มหน่วยงาน", en: "Site Group", type: "enum", options: ["OMA", "O&M", "Project", "GEM"] }, // I
  { key: "location_remark", th: "หมายเหตุ", en: "Location Remark", type: "text" },                             // J
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                             // K
];


/**
 * Column definitions for the ทะเบียนเครื่องจักร data set (Template No. 19 — 19_Machine_Equipment.xlsx) — table `assets`
 * ผ่านฟอร์มขึ้นทะเบียนเครื่องจักร (page/admin/assets.js). Sheet "Machine Equipment Template" — header แถว 8, ข้อมูลเริ่มแถว 9 (A..AN)
 * บังคับ: B รหัสไซต์งาน (lookup หน่วยงาน) · F ชื่อเครื่องจักร · G ระบบ (lookup จัดกลุ่มเครื่องจักร → รหัสกลุ่ม) · AN สถานะ
 * E รหัสเครื่องจักร = คีย์ upsert: มีในระบบ -> update, ว่าง -> สร้างใหม่และระบบออกรหัสให้ (ไฟล์ที่ส่งออกมีรหัสครบ นำเข้ากลับได้ทันที)
 * C เลขที่เอกสาร / AF PIN แผนที่ ระบบออกให้/คำนวณเอง -> ส่งออกอย่างเดียว ไม่นำเข้า
 * H, M..V เก็บลง assets.specifications · AG..AL เก็บลง assets.registration_checklist (มี/ไม่มี ต่อกลุ่ม — เว้นว่าง = ไม่แก้ค่าเดิม)
 */
const MACHINE_YES_NO_ALIASES = { yes: ["มี", "Yes", "Y", "ใช่", "true", "1"], no: ["ไม่มี", "No", "N", "ไม่", "ไม่ใช่", "false", "0"] };
// ป้ายในชีท Reference ของ template -> key ของ registration_checklist.training_levels (REG_TRAINING_LEVELS ฝั่งฟอร์ม)
const MACHINE_TRAINING_LEVEL_ORDER = ["basic", "advance", "apply"];
const MACHINE_TRAINING_LEVEL_LABELS = { basic: "อบรมขั้นพื้นฐาน (Basic)", advance: "อบรมขั้นสูง (Advance Level)", apply: "อบรมขั้นประยุกต์ใช้ (Apply Level)" };
const MACHINE_TRAINING_LEVEL_ALIASES = {
  basic: [MACHINE_TRAINING_LEVEL_LABELS.basic, "Basic", "Basic Level", "ขั้นพื้นฐาน", "พื้นฐาน", "อบรมขั้นพื้นฐาน"],
  advance: [MACHINE_TRAINING_LEVEL_LABELS.advance, "Advance", "Advance Level", "Advanced", "ขั้นสูง", "อบรมขั้นสูง"],
  apply: [MACHINE_TRAINING_LEVEL_LABELS.apply, "Apply", "Apply Level", "ขั้นประยุกต์ใช้", "ประยุกต์ใช้", "อบรมขั้นประยุกต์ใช้"],
};
// กลุ่มของช่องติ๊กที่คอลัมน์ มี/ไม่มี แต่ละคอลัมน์คุม (ต้องตรงกับ REG_DOCUMENT_KEYS / REG_INSPECTION_KEYS / REG_TRAINING_COURSES ใน page/admin/assets.js)
const MACHINE_DOCUMENT_KEYS = [
  "general_manual", "electrical_manual", "mechanical_manual", "control_manual", "spare_part_drawing",
  "structure_drawing", "electrical_drawing", "mechanical_drawing", "spare_part_list",
];
const MACHINE_INSPECTION_KEYS = ["inspection_report", "adjustment_report"];
const MACHINE_TRAINING_COURSES = ["safety", "maintenance", "operation"];
const matchMachineTrainingLevel = (value) => {
  const v = String(value ?? "").trim().toLowerCase();
  if (!v) return null;
  return MACHINE_TRAINING_LEVEL_ORDER.find((key) => key === v || MACHINE_TRAINING_LEVEL_ALIASES[key].some((label) => label.toLowerCase() === v)) || null;
};
const MACHINE_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                                        // A
  { key: "department_code", th: "รหัสไซต์งาน", en: "Site Code", type: "text", maxLength: 50, required: true },                      // B (lookup)
  { key: "document_number", th: "เลขที่เอกสาร", en: "Document No.", type: "auto" },                                 // C (ระบบออกให้)
  { key: "received_date", th: "วันที่รับ", en: "Received Date (dd/mm/yyyy)", type: "date", maxLength: 50 },                         // D
  { key: "asset_code", th: "รหัสเครื่องจักร", en: "Asset Code", type: "text", maxLength: 100, unique: true },                        // E (upsert key)
  { key: "machine_name", th: "ชื่อเครื่องจักร/อุปกรณ์", en: "Machine Name", type: "text", maxLength: 200, required: true },          // F
  { key: "machine_group_code", th: "ระบบ", en: "System", type: "text", maxLength: 20, required: true },                            // G (lookup)
  { key: "machine_type", th: "ประเภทเครื่องจักร", en: "Machine Type", type: "text", maxLength: 200 },                               // H
  { key: "brand_name", th: "ชื่อผู้ผลิต/ยี่ห้อ", en: "Brand", type: "text", maxLength: 200 },                                       // I
  { key: "model_name", th: "ชนิดรุ่น", en: "Type/Model", type: "text", maxLength: 200 },                                            // J
  { key: "serial_number", th: "หมายเลขเครื่อง", en: "Serial No.", type: "text", maxLength: 100, unique: true },                      // K
  { key: "price", th: "ราคา", en: "Price", type: "number" },                                                        // L
  { key: "capacity", th: "ขนาดความจุ", en: "Capacity", type: "text", maxLength: 200 },                                              // M
  { key: "width_mm", th: "กว้าง (มม.)", en: "Width (mm)", type: "number", maxLength: 50 },                                          // N
  { key: "length_mm", th: "ยาว (มม.)", en: "Length (mm)", type: "number", maxLength: 50 },                                          // O
  { key: "height_mm", th: "สูง (มม.)", en: "Height (mm)", type: "number", maxLength: 50 },                                          // P
  { key: "weight", th: "น้ำหนัก", en: "Weight", type: "number", maxLength: 50 },                                                   // Q
  { key: "weight_unit", th: "หน่วยของน้ำหนัก", en: "Weight Unit", type: "text", maxLength: 50 },                                   // R
  { key: "electric", th: "ขนาดกำลังไฟฟ้า", en: "Electric Consumption", type: "text", maxLength: 200 },                               // S
  { key: "power_kw", th: "กำลังไฟฟ้า (kW)", en: "Power (kW)", type: "number", maxLength: 50 },                                      // T
  { key: "utility", th: "สิ่งอำนวยความสะดวกที่ต้องการ", en: "Utility & Facility Supply", type: "text", maxLength: 2000 },             // U
  { key: "machine_remark", th: "หมายเหตุเครื่องจักร", en: "Machine Remark", type: "text", maxLength: 5000 },                          // V
  { key: "location_name", th: "ชื่อสถานที่", en: "Location Name", type: "text", maxLength: 200 },                                   // W
  { key: "location_address", th: "ที่อยู่", en: "Address", type: "text", maxLength: 2000 },                                           // X
  { key: "location_province", th: "จังหวัด", en: "Province", type: "text", maxLength: 100 },                                        // Y
  { key: "location_district", th: "อำเภอ", en: "District", type: "text", maxLength: 100 },                                          // Z
  { key: "location_sub_district", th: "ตำบล", en: "Sub-district", type: "text", maxLength: 100 },                                   // AA
  { key: "location_zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text", maxLength: 20 },                                // AB
  { key: "location_floor_room", th: "ชั้น / ห้อง", en: "Floor / Room", type: "text", maxLength: 100 },                              // AC
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number", maxLength: 50 },                                               // AD
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number", maxLength: 50 },                                            // AE
  { key: "map_pin", th: "PIN จากแผนที่", en: "Map PIN", type: "auto" },                                             // AF (คำนวณจากพิกัด)
  { key: "has_documents", th: "เอกสารคู่มือ/แบบแปลน/อะไหล่", en: "Manual / Drawing / Spare Part List", aliases: ["เอกสารคู่มือ, แบบแปลน Drawing และอะไหล่ Spare Part List"], type: "enum", enumAliases: MACHINE_YES_NO_ALIASES }, // AG
  { key: "has_training_document", th: "เอกสารประกอบการฝึกอบรม", en: "Training Document", type: "enum", enumAliases: MACHINE_YES_NO_ALIASES },            // AH
  { key: "training_levels", th: "ระดับของการฝึกอบรม", en: "Training Course Level", type: "list" },                   // AI (คั่นด้วย , ได้หลายระดับ)
  { key: "training_date", th: "วันที่ฝึกอบรม", en: "Training Date (dd/mm/yyyy)", type: "date", maxLength: 50 },                    // AJ
  { key: "has_training_evaluation", th: "บันทึกและประเมินการฝึกอบรม", en: "Training Evaluation", aliases: ["บันทึกและประเมินการฝึกอบรมม", "Training Evaluation and Record Form"], type: "enum", enumAliases: MACHINE_YES_NO_ALIASES }, // AK
  { key: "has_inspection_report", th: "รายงานการทดสอบก่อนตรวจรับ", en: "Machine Inspection Report", aliases: ["รายงานการทดสอบก่อนการตรวจรับงาน Machine Inspection Report"], type: "enum", enumAliases: MACHINE_YES_NO_ALIASES }, // AL
  { key: "remark", th: "หมายเหตุ", en: "Remark", type: "text" },                                                    // AM
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                                  // AN
];

/**
 * Column definitions for the Department (แผนก) data set (Template No. 18) — table `department_sections`.
 * Sheet "Department Template" — headers on row 8, data starts at row 9 (A..E).
 * UI name = "แผนก / Department"; payload keys เป็น section_* ตาม API (code `section` = แผนก).
 * `department_code` (Site Code, column D) is a Lookup against the company's sites (หน่วยงาน).
 * Upsert key = section_code (company-scoped, case-insensitive).
 */
const SECTION_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                                // A
  { key: "section_code", th: "รหัสแผนก", en: "Department Code", type: "text", required: true, unique: true }, // B
  { key: "name", th: "ชื่อแผนก", en: "Department Name", type: "text", required: true },                      // C
  { key: "department_code", th: "รหัสหน่วยงาน", en: "Site Code", type: "text", required: true },             // D (lookup)
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                           // E
];

// yyyy-mm-dd (DB) -> dd.mm.yyyy ตามรูปแบบวันที่ของไฟล์ SAP เพื่อให้ไฟล์ที่ส่งออกหน้าตาเหมือนไฟล์ต้นทาง
const toSapDate = (value) => {
  const m = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
};

// ISO datetime (DB, UTC) -> "dd.mm.yyyy, HH:MM:SS" (UTC) ตามคอลัมน์ "Created On (UTC)"
const toSapDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
};


/**
 * Per-data-set import/export configuration.
 * Only `location` is fully wired for now — the remaining data sets fall back to a
 * "not available yet" notice until their templates / endpoints are ready.
 */
/**
 * Column definitions for the Technician data set (Template No. 15).
 * Sheet "Technician Template" — headers on row 7, data starts at row 8 (A..V, 22 columns).
 * Columns MUST stay in exact A..V order so cell indices line up with the sheet.
 * `bank_code`, the two prefix columns and `vehicle_type` are Lookup columns read from the
 * "Reference" sheet (enumKeyMode "label" -> stored verbatim, e.g. "SCB" / "นาย" / "car").
 * `skills` is a comma-separated list of Material Group codes; the backend resolves each code
 * to the model uid stored in technician_skills.item. `department_code` is matched against the
 * department master (code, not name). Vehicle columns describe one vehicle per row and are
 * written to technician_vehicles.
 * Every imported technician gets a login user: `password` blank -> the backend falls back to
 * the technician_code, and the role is picked once in the preview modal.
 * Upsert key = technician_code (company-scoped) — รหัสซ้ำจะอัปเดตทับของเดิม ไม่สร้างใหม่.
 */
const TECHNICIAN_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "Row No.", type: "auto" },                                              // A
  { key: "technician_code", th: "รหัสช่าง", en: "Technician Code", type: "text", required: true, unique: true }, // B
  { key: "perfix_name", th: "คำนำหน้า (ไทย)", en: "Title TH", type: "enum", enumKeyMode: "label" },        // C
  { key: "first_name_th", th: "ชื่อ (ไทย)", en: "First Name TH", type: "text", required: true },           // D
  { key: "last_name_th", th: "นามสกุล (ไทย)", en: "Last Name TH", type: "text", required: true },          // E
  { key: "nickname_th", th: "ชื่อเล่น (ไทย)", en: "Nickname TH", type: "text" },                           // F
  { key: "perfix_name_en", th: "คำนำหน้า (อังกฤษ)", en: "Title EN", type: "enum", enumKeyMode: "label" },  // G
  { key: "first_name_en", th: "ชื่อ (อังกฤษ)", en: "First Name EN", type: "text" },                        // H
  { key: "last_name_en", th: "นามสกุล (อังกฤษ)", en: "Last Name EN", type: "text" },                       // I
  { key: "nickname_en", th: "ชื่อเล่น (อังกฤษ)", en: "Nickname EN", type: "text" },                        // J
  { key: "phone", th: "โทรศัพท์", en: "Phone", type: "text", required: true },                             // K
  { key: "email", th: "อีเมล", en: "Email", type: "email", required: true, unique: true },                 // L (= username ของ user)
  { key: "bank_code", th: "ธนาคาร", en: "Bank", type: "enum", enumKeyMode: "label" },                      // M
  { key: "bank_account_number", th: "เลขที่บัญชีธนาคาร", en: "Bank Account No.", type: "text" },           // N
  { key: "bank_account_name", th: "ชื่อบัญชีธนาคาร", en: "Bank Account Name", type: "text" },              // O
  { key: "vehicle_type", th: "ประเภทพาหนะ", en: "Vehicle Type", type: "enum", enumKeyMode: "label" },      // P
  { key: "vehicle_brand_model", th: "ยี่ห้อ / รุ่นพาหนะ", en: "Vehicle Brand / Model", type: "text" },     // Q
  { key: "vehicle_license_plate", th: "ป้ายทะเบียน", en: "License Plate", type: "text" },                  // R
  { key: "skills", th: "ความชำนาญ (รหัสรุ่น)", en: "Skills (Model Codes)", type: "list", aliases: ["Skills (Material Group Codes)"] },  // S (lookup)
  { key: "department_code", th: "รหัสหน่วยงาน", en: "Site Code", type: "text", required: true, aliases: ["Department Code"] }, // T (lookup) ไฟล์เก่าใช้หัว Department Code
  { key: "password", th: "รหัสผ่าน", en: "Password", type: "text" },                                       // U
  { key: "status", th: "สถานะ", en: "Status", type: "status", required: true },                            // V
];

/**
 * Column definitions for the Asset / Machine data set (Template No. 10).
 * Sheet "Data Template" — row 7 = section titles, row 8 = column headers -> data starts at row 9.
 * Columns MUST stay in exact A..AJ order so cell indices line up with the sheet.
 * `th` / `en` are copied verbatim from the template header so the preview table reads exactly
 * like the file (the template's own TH/EN pairing for I/J is inconsistent — keys carry the
 * correct meaning: I = Material Group / models, J = Material Type / brands. See the glossary
 * in components/global.js).
 * Lookup columns carry the master's CODE and are resolved to ids on the backend:
 *   project_code -> projects · department_code -> departments · customer_code -> customers
 *   sale_code -> sale_masters · part_number -> part_masters
 * Auto columns (G-J, Y, AG, AJ) are filled by the system on export and ignored on import,
 * matching the note printed on the template header.
 * Upsert key = serial_number (company-scoped) — หมายเลขเครื่องเดิมจะอัปเดตทับ ไม่สร้างใหม่
 * และ asset_code เป็นเลขรันนิ่งที่ระบบออกให้ตอนสร้างเท่านั้น จึงไม่มีคอลัมน์นี้ในไฟล์.
 */
const ASSET_COLUMNS = [
  { key: "no", th: "ลำดับที่", en: "No.", type: "auto" },                                                  // A
  { key: "project_code", th: "โครงการ", en: "Project", type: "text", required: true },                     // B (lookup)
  { key: "department_code", th: "หน่วยงาน", en: "Department", type: "text" },                                  // C (lookup)
  { key: "customer_code", th: "รหัสลูกค้า", en: "Customer ID", type: "text", required: true },             // D (lookup)
  { key: "sale_code", th: "พนักงานขาย", en: "Sales ID", type: "text" },                                    // E (lookup)
  { key: "part_number", th: "ITEM ID (รหัสสินค้า)", en: "Item ID", type: "text", required: true },          // F (lookup)
  { key: "part_name", th: "ชื่อสินค้า", en: "Product Name", type: "auto" },                                // G
  { key: "serie_name", th: "ซีรีส์", en: "Series", type: "auto" },                                        // H
  { key: "model_name", th: "รุ่น", en: "Model", type: "auto" },                                           // I (= models)
  { key: "brand_name", th: "แบรนด์", en: "Brand", type: "auto" },                                           // J (= brands)
  { key: "serial_number", th: "หมายเลขเครื่อง", en: "Serial No.", type: "text", required: true, unique: true }, // K
  { key: "price", th: "ราคาเครื่อง", en: "Machine Price", type: "number" },                                // L
  { key: "is_active", th: "สถานะ", en: "Status", type: "status", required: true },                         // M
  { key: "replacement_period", th: "รอบการเปลี่ยนอะไหล่", en: "Replacement Period", type: "number" },      // N
  { key: "location_running_number", th: "รหัสสถานที่", en: "Location ID", type: "text" },                  // O
  { key: "location_name", th: "ชื่อสถานที่", en: "Location Name", type: "text" },                          // P
  { key: "location_address", th: "ที่อยู่", en: "Address", type: "text" },                                  // Q
  { key: "location_province", th: "จังหวัด", en: "Province", type: "text" },                                // R
  { key: "location_district", th: "อำเภอ", en: "District", type: "text" },                                  // S
  { key: "location_sub_district", th: "ตำบล", en: "Sub-district", type: "text" },                          // T
  { key: "location_zip_code", th: "รหัสไปรษณีย์", en: "Postal Code", type: "text" },                       // U
  { key: "location_floor_room", th: "ชั้น / ห้อง", en: "Floor / Room", type: "text" },                     // V
  { key: "latitude", th: "ละติจูด", en: "Latitude", type: "number" },                                       // W
  { key: "longitude", th: "ลองจิจูด", en: "Longitude", type: "number" },                                    // X
  { key: "map_pin", th: "ปักหมุดจากแผนที่", en: "Pin from Map", type: "auto" },                             // Y (no field on the asset)
  { key: "contract_name", th: "ชื่อผู้ติดต่อ", en: "Contract Name", type: "text" },                        // Z
  { key: "contract_phone", th: "เบอร์โทรศัพท์", en: "Phone Number", type: "text" },                         // AA
  { key: "contract_email", th: "อีเมล", en: "Email", type: "email" },                                       // AB
  { key: "location_remark", th: "หมายเหตุสถานที่", en: "Location Note", type: "text" },                     // AC
  { key: "invoice_number", th: "เลขใบกำกับภาษี", en: "Invoice No", type: "text" },                          // AD
  { key: "invoice_date", th: "วันที่ใบกำกับภาษี", en: "Invoice Date (dd/mm/yyyy)", type: "date" },          // AE
  { key: "coi_date", th: "วันที่ติดตั้ง (EOI)", en: "EOI Date (dd/mm/yyyy)", type: "date" },                // AF
  // ประกันเริ่มนับจากวันที่ติดตั้ง (EOI) อยู่แล้ว -> ส่งออกให้ตรงกับ AF และไม่นำเข้าซ้ำ
  { key: "warranty_start_date", th: "วันที่เริ่มประกัน", en: "Warranty Start Date (dd/mm/yyyy)", type: "auto" }, // AG
  { key: "customer_warranty_month", th: "ระยะเวลาประกันลูกค้า (เดือน)", en: "Customer Warranty Ext. (Month)", type: "number" }, // AH
  { key: "customer_warranty_expire_date", th: "วันหมดประกันลูกค้า", en: "Customer Warranty End (dd/mm/yyyy)", type: "date" },   // AI
  // จำนวนงวดประกันคำนวณจากรายการประกันจริง (หน้าทรัพย์สินก็แสดงเป็นช่องอ่านอย่างเดียว)
  // ไฟล์พาข้อมูลประกันมาได้งวดเดียวต่อแถว จึงส่งออกอย่างเดียว ไม่นำเข้า
  { key: "customer_warranty_count", th: "จำนวนครั้งที่ต่ออายุการรับประกัน", en: "Warranty Ext. (Renewal Count)", type: "auto" }, // AJ
];

/**
 * ISO / Date -> "dd/mm/yyyy" ตามรูปแบบที่ template กำหนด (ค่าว่างคืนสตริงว่าง)
 * ใช้ตอนส่งออกเท่านั้น — ขานำเข้าใช้ normDate ซึ่งรับทั้ง dd/mm/yyyy, ISO และ serial ของ Excel
 */
const toTemplateDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// permission ของปุ่ม "นำเข้า" แต่ละชุดข้อมูล — ต้องตรงกับ checkPermission ของ route นำเข้าฝั่ง backend
const IMPORT_PERMISSION = {
  customer: "customers.create",
  technician: "technicians.create",
  site: "departments.create",
  section: "sections.create",
  part_master: "part-master.create",
  asset: "asset.create",
  machine: "asset.create",
  location: "location.create",
  warehouse: "warehouse.create",
  uom: "uoms.create",
  category: "part-categories.create",
  brand: "brands.create",
  model: "models.create",
  project: "projects.create",
  wbs: "projects.create",
  stock: "stock.import",
};

const DATASET_CONFIG = {
  customer: {
    endpoint: "admin/customers",
    importPath: "admin/customers/import", // dedicated bulk upsert by customer_code
    templateAsset: customerTemplateFile,
    templateFile: "01_Customer_Template.xlsx",
    exportFile: "Customer_Export.xlsx",
    sheetName: "Customer Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: CUSTOMER_COLUMNS,
    // Reference sheet lists values in column B (column A is the running No.) -> valueColumn 1.
    reference: {
      sheetMatch: "Reference",
      valueColumn: 1,
      sections: [
        { columnKey: "type", startsWith: "ประเภทลูกค้า" },
        { columnKey: "group", startsWith: "กลุ่มลูกค้า" },
        { columnKey: "source", startsWith: "แหล่งที่มา" },
        { columnKey: "contact_prefix", startsWith: "คำนำหน้า" },
        { columnKey: "payment_type", startsWith: "วิธีการชำระเงิน" },
        { columnKey: "currency", startsWith: "สกุลเงิน" },
        { columnKey: "sla_level", startsWith: "ระดับ SLA" },
      ],
    },
    // API record -> row aligned to `columns` (display labels match the template/Reference).
    toRow: (item, idx) => [
      idx + 1,
      item.customer_code || "",
      item.full_name_th || "",
      item.alias_name || "",
      item.full_name_en || "",
      item.type || "",
      item.group || "",
      item.tax_number || "",
      item.active_start_date || "",
      item.source || "",
      item.address || "",
      item.province || "",
      item.district || "",
      item.zip_code || "",
      item.phone || "",
      item.phone_2 || "",
      item.fax || "",
      item.email || "",
      item.website || "",
      item.contact_prefix || "",
      item.contact_first_name || "",
      item.contact_last_name || "",
      item.contact_position || "",
      item.contact_phone || "",
      item.contact_email || "",
      item.payment_type || "",
      item.currency || "",
      item.priority?.name_en || "",
      item.account_owner || "",
      item.remark || "",
      item.customer_status || (item.is_active ? "Active" : "Inactive"),
    ],
    // validated cells -> upsert payload (priority resolved from sla_level on the backend).
    // Only the template's columns are sent; fields outside the template are left untouched.
    toPayload: (cells) => ({
      company_id: company?.id || "",
      customer_code: cells.customer_code.value,
      full_name_th: cells.full_name_th.value,
      full_name_en: cells.full_name_en?.value || "",
      alias_name: cells.alias_name?.value || "",
      type: cells.type.value,
      group: cells.group?.value || "",
      tax_number: cells.tax_number?.value || "",
      active_start_date: cells.active_start_date?.value || "",
      source: cells.source?.value || "",
      address: cells.address.value,
      province: cells.province.value,
      district: cells.district?.value || "",
      zip_code: cells.zip_code?.value || "",
      phone: cells.phone?.value || "",
      phone_2: cells.phone_2?.value || "",
      fax: cells.fax?.value || "",
      email: cells.email?.value || "",
      website: cells.website?.value || "",
      contact_prefix: cells.contact_prefix?.value || "",
      contact_first_name: cells.contact_first_name.value,
      contact_last_name: cells.contact_last_name?.value || "",
      contact_position: cells.contact_position?.value || "",
      contact_phone: cells.contact_phone?.value || "",
      contact_email: cells.contact_email?.value || "",
      payment_type: cells.payment_type?.value || "",
      currency: cells.currency?.value || "",
      sla_level: cells.sla_level?.value || "",
      account_owner: cells.account_owner?.value || "",
      remark: cells.remark?.value || "",
      customer_status: cells.customer_status.value, // "Active" | "Inactive" | "Hold"
      is_active: cells.customer_status.value === "Active",
    }),
  },

  technician: {
    endpoint: "admin/technicians",
    importPath: "admin/technicians/import", // dedicated bulk upsert (technician_code + company)
    templateAsset: technicianTemplateFile,
    templateFile: "15_Technician_Template.xlsx",
    exportFile: "Technician_Export.xlsx",
    sheetName: "Technician Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: TECHNICIAN_COLUMNS,
    // Reference sheet lists values in column B (column A is the running No.) -> valueColumn 1.
    reference: {
      sheetMatch: "Reference",
      valueColumn: 1,
      sections: [
        { columnKey: "perfix_name", startsWith: "คำนำหน้าชื่อ (ไทย)" },
        { columnKey: "perfix_name_en", startsWith: "คำนำหน้าชื่อ (อังกฤษ)" },
        { columnKey: "bank_code", startsWith: "รหัสธนาคาร" },
        { columnKey: "vehicle_type", startsWith: "ประเภทพาหนะ" },
        { columnKey: "status", startsWith: "สถานะ" },
      ],
    },
    // บทบาทไม่ได้อยู่ในไฟล์ (เป็นเรื่องสิทธิ์ ไม่ใช่ข้อมูลช่าง และไฟล์หนึ่งชุดมักเป็นบทบาทเดียวกันทั้งไฟล์)
    // -> ให้เลือกครั้งเดียวใน modal ก่อนยืนยัน แล้วส่งไปที่ระดับ body ใช้กับทุกแถว
    bulkOption: {
      bodyField: "role_uid",
      label: "role",
      placeholder: "select_role",
      hint: "import_role_hint",
      endpoint: "admin/roles",
      valueField: "uid",
      labelField: "name",
    },
    // แต่ละ chunk สร้าง user (login) ให้ช่างใหม่ด้วย และ user_code เดินเลขจากค่าล่าสุดใน DB
    // -> ต้องส่งทีละ chunk ไม่งั้น chunk ที่วิ่งพร้อมกันจะได้ user_code ชนกัน
    sequentialImport: true,
    // Company-scoped masters checked in the preview; the backend re-resolves them on save.
    // ทั้งสองตัว backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      skills: { endpoint: "warehouse/models", field: "model_code", lower: true },
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
    },
    // technician_skills.item เก็บเป็น uid ของยี่ห้อ (models) -> ตอน export ต้อง map กลับเป็นรหัส
    // เพื่อให้ไฟล์ที่ส่งออกนำกลับเข้ามา import ซ้ำได้ทันที
    exportLookups: {
      skills: { endpoint: "warehouse/models", field: "uid", mapField: "model_code" },
    },
    // ระบุประเภทพาหนะแล้วต้องมีป้ายทะเบียนด้วย (กฎเดียวกับปุ่มเพิ่มพาหนะในหน้าช่าง)
    postValidate: (rows) => {
      rows.forEach((r) => {
        const type = (r.cells.vehicle_type?.raw || "").trim();
        const plate = r.cells.vehicle_license_plate;
        if (type && plate && !(plate.raw || "").trim()) {
          plate.error = "required";
          r.valid = false;
        }
      });
      return rows;
    },
    // Per-row display/normalization applied after validation:
    //  - Bank Account Name defaults to the technician's Thai full name when blank (same as the form)
    //  - the password is masked in the preview table
    enrichRows: (rows) => {
      const isBlank = (v) => { const s = String(v ?? "").trim(); return s === "" || s === "-"; };
      rows.forEach((r) => {
        const c = r.cells;
        if (c.bank_account_name && isBlank(c.bank_account_name.raw)) {
          const name = `${c.first_name_th?.value || ""} ${c.last_name_th?.value || ""}`.trim();
          if (name) { c.bank_account_name.display = name; c.bank_account_name.value = name; }
        }
        // รหัสผ่านจริงไม่ควรโชว์ในตาราง ส่วนช่องที่เว้นว่างบอกให้เห็นว่าระบบจะใช้รหัสช่างแทน
        if (c.password) {
          c.password.display = String(c.password.raw ?? "").trim() !== ""
            ? "••••••"
            : `${t("import_password_default_hint")}: ${c.technician_code?.value || ""}`;
        }
      });
      return rows;
    },
    // API record -> row aligned to `columns` (display labels match the template/Reference).
    // Skill items are Material Group uids -> mapped back to codes via `exportLookups`.
    // พาหนะส่งออกคันแรก (template รองรับ 1 คันต่อแถว)
    toRow: (item, idx, ctx) => {
      const codeOf = ctx?.maps?.skills;
      const skills = (item.technician_skill || [])
        .map((s) => (codeOf ? codeOf.get(String(s.item || "")) : "") || "")
        .filter(Boolean)
        .join(", ");
      const vehicle = (item.technician_vehicle || [])[0] || {};
      return [
        idx + 1,
        item.technician_code || "",
        item.perfix_name || "",
        item.first_name_th || "",
        item.last_name_th || "",
        item.nickname_th || "",
        item.perfix_name_en || "",
        item.first_name_en || "",
        item.last_name_en || "",
        item.nickname_en || "",
        item.phone || "",
        item.email || "",
        item.bank_code || "",
        item.bank_account_number || "",
        item.bank_account_name || "",
        vehicle.vehicle_type || "",
        vehicle.brand_model || "",
        vehicle.license_plate || "",
        skills,
        item.department_code || "",
        "", // Password is never exported
        item.is_active ? "Active" : "Inactive",
      ];
    },
    // validated cells -> upsert payload (skills / department resolved on the backend)
    toPayload: (cells) => {
      const bank = (global.bank || []).find((b) => b.value === cells.bank_code?.value);
      return {
        company_id: company?.id || "",
        technician_code: cells.technician_code.value,
        perfix_name: cells.perfix_name?.value || "",
        perfix_name_en: cells.perfix_name_en?.value || "",
        first_name_th: cells.first_name_th.value,
        last_name_th: cells.last_name_th.value,
        first_name_en: cells.first_name_en?.value || "",
        last_name_en: cells.last_name_en?.value || "",
        nickname_th: cells.nickname_th?.value || "",
        nickname_en: cells.nickname_en?.value || "",
        phone: cells.phone.value,
        email: cells.email.value,
        bank_code: cells.bank_code?.value || "",
        bank_name: bank?.name_th || cells.bank_code?.value || "",
        bank_account_number: cells.bank_account_number?.value || "",
        bank_account_name: cells.bank_account_name?.value || "",
        vehicle_type: cells.vehicle_type?.value || "",
        vehicle_brand_model: cells.vehicle_brand_model?.value || "",
        vehicle_license_plate: cells.vehicle_license_plate?.value || "",
        skills: Array.isArray(cells.skills?.value) ? cells.skills.value : [],
        department_code: cells.department_code.value,
        password: cells.password?.value || "",
        status: cells.status.value === true ? "active" : "inactive",
        is_active: cells.status.value === true,
      };
    },
  },

  asset: {
    endpoint: "admin/assets",
    importPath: "admin/assets/import", // bulk upsert by serial_number + resolve relations
    templateAsset: assetTemplateFile,
    templateFile: "10_Asset_Template.xlsx",
    exportFile: "Asset_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 8, // row 7 = section titles, row 8 = column headers -> data from row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: ASSET_COLUMNS,
    // asset_code เป็นเลขรันนิ่งต่อ prefix ที่อ่านจากรหัสล่าสุดใน DB -> chunk ที่วิ่งพร้อมกัน
    // จะได้เลขชนกัน (เหตุผลเดียวกับ user_code ของชุดข้อมูลช่าง) จึงต้องส่งทีละ chunk
    sequentialImport: true,
    // Company-scoped masters checked in the preview; the backend re-resolves them on save.
    // รหัสหน่วยงาน backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      project_code: { endpoint: "admin/projects", field: "project_code" },
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
      customer_code: { endpoint: "admin/customers", field: "customer_code" },
      sale_code: { endpoint: "admin/sale-masters", field: "sale_code" },
      part_number: { endpoint: "warehouse/part-masters", field: "part_number" },
    },
    // API record -> row aligned to `columns`. ชื่อสินค้า/โมเดล (series)/ยี่ห้อ (models)/แบรนด์ (brands) มาจาก Item Master
    // (คอลัมน์ "โมเดล" API ทรัพย์สินไม่ได้ส่งกลับมา -> ปล่อยว่างไว้ ไม่กระทบการนำเข้าเพราะเป็นคอลัมน์ auto)
    // ประกันลูกค้าในไฟล์มีงวดเดียว -> ส่งออกงวดล่าสุด ซึ่งเป็นงวดที่ยังมีผลอยู่
    toRow: (item, idx) => {
      const warranties = item.customer_warranty || [];
      const warranty = warranties[warranties.length - 1] || {};
      return [
        idx + 1,
        item.project?.project_code || "",
        item.department?.department_code || "",
        item.customer?.customer_code || "",
        item.sale?.sale_code || "",
        item.part_number || item.part?.part_number || "",
        item.part_name || item.part?.name || "",
        "",
        item.part?.model?.name || "",
        item.part?.brand?.name || "",
        item.serial_number || "",
        item.price ?? "",
        item.is_active ? "Active" : "Inactive",
        item.replacement_period ?? "",
        item.location_running_number || "",
        item.location_name || "",
        item.location_address || "",
        item.location_province || "",
        item.location_district || "",
        item.location_sub_district || "",
        item.location_zip_code || "",
        item.location_floor_room || "",
        item.latitude || "",
        item.longitude || "",
        "",
        item.contract_name || "",
        item.contract_phone || "",
        item.contract_email || "",
        item.location_remark || "",
        item.invoice_number || "",
        toTemplateDate(item.invoice_date),
        toTemplateDate(item.coi_date),
        toTemplateDate(item.coi_date), // ประกันเริ่มนับจากวันที่ติดตั้ง
        warranty.customer_warranty_month ?? "",
        toTemplateDate(warranty.customer_warranty_expire_date),
        item.customer_warranty_count ?? "",
      ];
    },
    // validated cells -> upsert payload (relations resolved from their codes on the backend)
    toPayload: (cells) => {
      const num = (k) => {
        const v = cells[k]?.value;
        return v !== "" && v != null && isFinite(Number(v)) ? Number(v) : null;
      };
      const int = (k) => {
        const n = num(k);
        return n == null ? null : Math.trunc(n);
      };
      const money = (k) => {
        const n = num(k);
        return n == null ? null : Math.round(n * 100) / 100;
      };
      return {
        company_id: company?.id || "",
        project_code: cells.project_code.value,
        department_code: cells.department_code?.value || "",
        customer_code: cells.customer_code.value,
        sale_code: cells.sale_code?.value || "",
        part_number: cells.part_number.value,
        serial_number: cells.serial_number.value,
        price: money("price"),
        replacement_period: int("replacement_period"),

        location_running_number: cells.location_running_number?.value || "",
        location_name: cells.location_name?.value || "",
        location_address: cells.location_address?.value || "",
        location_province: cells.location_province?.value || "",
        location_district: cells.location_district?.value || "",
        location_sub_district: cells.location_sub_district?.value || "",
        location_zip_code: cells.location_zip_code?.value || "",
        location_floor_room: cells.location_floor_room?.value || "",
        latitude: cells.latitude?.value != null ? String(cells.latitude.value) : "",
        longitude: cells.longitude?.value != null ? String(cells.longitude.value) : "",
        location_remark: cells.location_remark?.value || "",

        contract_name: cells.contract_name?.value || "",
        contract_phone: cells.contract_phone?.value || "",
        contract_email: cells.contract_email?.value || "",

        invoice_number: cells.invoice_number?.value || "",
        invoice_date: cells.invoice_date?.value || "",
        coi_date: cells.coi_date?.value || "",

        customer_warranty_month: int("customer_warranty_month"),
        customer_warranty_expire_date: cells.customer_warranty_expire_date?.value || "",

        is_active: cells.is_active.value === true,
      };
    },
  },

  machine: {
    endpoint: "admin/assets",
    importPath: "admin/assets/import-machines", // upsert by asset_code + resolve site / machine group codes
    templateAsset: machineTemplateFile,
    templateFile: "19_Machine_Equipment.xlsx",
    exportFile: "Machine_Equipment_Export.xlsx",
    sheetName: "Machine Equipment Template",
    headerRows: 8, // rows 1-8 are header/instructions; data starts at row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: MACHINE_COLUMNS,
    // รหัสเครื่องจักร / เลขที่เอกสาร / รหัสสถานที่ ที่ระบบออกให้เดินเลขในหน่วยความจำต่อ chunk -> ส่งทีละ chunk กันเลขชนกัน
    sequentialImport: true,
    // backend resolve รหัสหน่วยงานและรหัสกลุ่มแบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
      machine_group_code: { endpoint: "admin/machine-groups", field: "code", lower: true },
    },
    // ระดับการฝึกอบรม (AI) รับได้หลายค่าคั่นด้วย , -> ทุกค่าต้องตรงกับชีท Reference; วันที่ฝึกอบรม (AJ) ต้องมาพร้อมระดับ
    postValidate: (rows) =>
      rows.map((row) => {
        const cells = { ...row.cells };
        let valid = row.valid;
        const levelCell = cells.training_levels;
        if (levelCell && !levelCell.error) {
          const keys = [];
          const unknown = [];
          (levelCell.value || []).forEach((item) => {
            const key = matchMachineTrainingLevel(item);
            if (!key) unknown.push(item);
            else if (!keys.includes(key)) keys.push(key);
          });
          if (unknown.length) {
            cells.training_levels = { ...levelCell, error: "enum", detail: unknown.join(", ") };
            valid = false;
          } else {
            cells.training_levels = { ...levelCell, value: keys };
          }
        }
        const dateCell = cells.training_date;
        if (dateCell && !dateCell.error && dateCell.raw !== "" && cells.training_levels && !cells.training_levels.error && !cells.training_levels.value.length) {
          cells.training_levels = { ...cells.training_levels, error: "required" };
          valid = false;
        }
        return { ...row, cells, valid };
      }),
    // API record -> row aligned to `columns` — ค่าในไฟล์ใช้ป้ายเดียวกับชีท Reference (มี/ไม่มี, Active/Inactive, ระดับอบรม)
    toRow: (item, idx) => {
      const spec = item.specifications || {};
      const checklist = item.registration_checklist || {};
      const documents = checklist.documents || {};
      const yesNo = (flag) => (flag ? "มี" : "ไม่มี");
      const levels = MACHINE_TRAINING_LEVEL_ORDER.filter((level) => checklist.training_levels?.[level]?.checked);
      // ไฟล์มีช่องวันที่ฝึกอบรมช่องเดียว -> ใช้วันที่ล่าสุดของระดับที่ติ๊ก
      const levelDates = levels.map((level) => checklist.training_levels[level]?.date).filter(Boolean).sort();
      const lat = item.latitude || "";
      const lng = item.longitude || "";
      return [
        idx + 1,
        item.department?.department_code || "",
        item.document_number || "",
        toTemplateDate(item.received_date),
        item.asset_code || "",
        item.machine_name || item.part_name || "",
        item.machine_group_code || "",
        spec.machine_type ?? "",
        item.brand_name || "",
        item.model_name || "",
        item.serial_number || "",
        item.price != null && item.price !== "" ? Number(item.price) : "",
        spec.capacity ?? "",
        spec.width_mm ?? "",
        spec.length_mm ?? "",
        spec.height_mm ?? "",
        spec.weight ?? "",
        spec.weight_unit ?? "",
        spec.electric ?? "",
        spec.power_kw ?? "",
        spec.utility ?? "",
        spec.machine_remark ?? "",
        item.location_name || "",
        item.location_address || "",
        item.location_province || "",
        item.location_district || "",
        item.location_sub_district || "",
        item.location_zip_code || "",
        item.location_floor_room || "",
        lat,
        lng,
        lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : "",
        yesNo(MACHINE_DOCUMENT_KEYS.some((key) => documents[key])),
        yesNo(MACHINE_TRAINING_COURSES.some((course) => checklist.training_courses?.[course])),
        levels.map((level) => MACHINE_TRAINING_LEVEL_LABELS[level]).join(", "),
        toTemplateDate(levelDates[levelDates.length - 1]),
        yesNo(Boolean(checklist.training_evaluation)),
        yesNo(MACHINE_INSPECTION_KEYS.some((key) => documents[key])),
        item.remark || "",
        item.is_active ? "Active" : "Inactive",
      ];
    },
    // validated cells -> upsert payload (รหัสหน่วยงาน/รหัสกลุ่ม resolve เป็น id ที่ backend)
    toPayload: (cells) => {
      const text = (key) => (cells[key]?.value == null ? "" : String(cells[key].value));
      const flag = (key) => cells[key]?.value || ""; // "yes" | "no" | "" (ว่าง = ไม่แก้ค่าเดิม)
      const price = cells.price?.value;
      return {
        company_id: company?.id || "",
        department_code: text("department_code"),
        received_date: text("received_date"),
        asset_code: text("asset_code"),
        machine_name: text("machine_name"),
        machine_group_code: text("machine_group_code"),
        machine_type: text("machine_type"),
        brand_name: text("brand_name"),
        model_name: text("model_name"),
        serial_number: text("serial_number"),
        price: price !== "" && price != null && isFinite(Number(price)) ? Math.round(Number(price) * 100) / 100 : null,

        capacity: text("capacity"),
        width_mm: text("width_mm"),
        length_mm: text("length_mm"),
        height_mm: text("height_mm"),
        weight: text("weight"),
        weight_unit: text("weight_unit"),
        electric: text("electric"),
        power_kw: text("power_kw"),
        utility: text("utility"),
        machine_remark: text("machine_remark"),

        location_name: text("location_name"),
        location_address: text("location_address"),
        location_province: text("location_province"),
        location_district: text("location_district"),
        location_sub_district: text("location_sub_district"),
        location_zip_code: text("location_zip_code"),
        location_floor_room: text("location_floor_room"),
        latitude: text("latitude"),
        longitude: text("longitude"),

        has_documents: flag("has_documents"),
        has_training_document: flag("has_training_document"),
        training_levels: cells.training_levels?.value || [],
        training_date: text("training_date"),
        has_training_evaluation: flag("has_training_evaluation"),
        has_inspection_report: flag("has_inspection_report"),

        remark: text("remark"),
        is_active: cells.is_active.value === true,
      };
    },
  },

  site: {
    endpoint: "admin/departments",
    importPath: "admin/departments/import", // bulk upsert by department_code (Site Code)
    templateAsset: siteTemplateFile,
    templateFile: "17_Site_Template.xlsx",
    exportFile: "Site_Export.xlsx",
    sheetName: "Site Template",
    headerRows: 8, // rows 1-8 are header/instructions; data starts at row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: SITE_COLUMNS,
    // เทมเพลตเติม "Active" ไว้ให้ทุกแถวจนถึงแถว 108 -> แถวที่มีแค่สถานะถือเป็นแถวว่าง ไม่ใช่แถวที่ลืมกรอกรหัส
    prefilledKeys: ["is_active"],
    toRow: (item, idx) => [
      idx + 1,
      item.department_code || "",
      item.name || "",
      item.address || "",
      item.province || "",
      item.district || "",
      item.sub_district || "",
      item.zip_code || "",
      item.site_group || "",
      item.location_remark || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      department_code: cells.department_code.value,
      name: cells.name?.value || "",
      address: cells.address?.value || "",
      province: cells.province?.value || "",
      district: cells.district?.value || "",
      sub_district: cells.sub_district?.value || "",
      zip_code: cells.zip_code?.value || "",
      site_group: cells.site_group?.value || "",
      location_remark: cells.location_remark?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  section: {
    endpoint: "admin/sections",
    importPath: "admin/sections/import", // bulk upsert by section_code (Department Code)
    templateAsset: sectionTemplateFile,
    templateFile: "18_Department_Template.xlsx",
    exportFile: "Department_Export.xlsx",
    sheetName: "Department Template",
    headerRows: 8, // rows 1-8 are header/instructions; data starts at row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: SECTION_COLUMNS,
    // รหัสหน่วยงาน backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.section_code || "",
      item.name || "",
      item.department?.department_code || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      section_code: cells.section_code.value,
      name: cells.name.value,
      department_code: cells.department_code.value,
      is_active: cells.is_active.value === true,
    }),
  },

  location: {
    endpoint: "warehouse/locations",
    importPath: "warehouse/locations/import", // bulk upsert endpoint (implemented)
    templateAsset: locationTemplateFile,
    templateFile: "12_Location_Template.xlsx",
    exportFile: "Location_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 7, // rows 1-7 are header/instructions; data starts at row 8
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: LOCATION_COLUMNS,
    // Reference sheet — valid values for enum/status columns are read from here.
    reference: {
      sheetMatch: "Reference",
      sections: [
        { columnKey: "location_type", startsWith: "ประเภทตำแหน่ง" },
        { columnKey: "is_active", startsWith: "สถานะ" },
      ],
    },
    // API record -> row aligned to `columns` (use display labels so it matches the template/Reference)
    toRow: (item, idx) => [
      idx + 1,
      item.location_code || "",
      item.name || "",
      item.location_type ? t(item.location_type) : "",
      item.address || "",
      item.latitude || "",
      item.longitude || "",
      item.description || "",
      item.is_active ? "เปิดใช้งาน" : "ปิดใช้งาน",
    ],
    // validated cells -> create payload
    toPayload: (cells) => ({
      company_id: company?.id || "",
      location_code: cells.location_code.value,
      name: cells.name.value,
      location_type: cells.location_type.value,
      address: cells.address?.value || "",
      latitude: cells.latitude?.value ? String(cells.latitude.value) : "",
      longitude: cells.longitude?.value ? String(cells.longitude.value) : "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  warehouse: {
    endpoint: "warehouse/warehouses",
    importPath: "warehouse/warehouses/import", // bulk upsert by warehouse_code + links site & storage locations
    templateAsset: warehouseTemplateFile,
    templateFile: "11_Warehouse_Template.xlsx",
    exportFile: "Warehouse_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 8, // rows 1-8 are header/instructions; data starts at row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: WAREHOUSE_COLUMNS,
    // พื้นที่เก็บย้ายคลังข้ามแถวในไฟล์ได้ -> ส่งทีละ chunk ตามลำดับ ผลลัพธ์จะได้ไม่ขึ้นกับ chunk ไหนถึงก่อน
    sequentialImport: true,
    // Lookups fetched (company-scoped) before validation -> { columnKey: { endpoint, field } }.
    // รหัสหน่วยงาน backend resolve แบบไม่สนตัวพิมพ์ ฝั่งพรีวิวจึงต้องเทียบแบบเดียวกัน
    lookups: {
      department_code: { endpoint: "admin/departments", field: "department_code", lower: true },
      // mapField -> รหัสคลังที่พื้นที่เก็บผูกอยู่ตอนนี้ ("" = ยังว่าง) ใช้ตรวจกฎ 1 พื้นที่เก็บต่อ 1 คลัง
      storage_locations: { endpoint: "warehouse/locations", field: "location_code", mapField: (d) => d.warehouse?.warehouse_code || "" },
    },
    // 1 พื้นที่เก็บอยู่ได้แค่ 1 คลัง (กฎเดียวกับ backend):
    //   - รหัสเดียวกันอยู่มากกว่า 1 แถวในไฟล์ -> duplicate
    //   - ผูกกับคลังอื่นที่ไม่ได้อยู่ในไฟล์นี้ -> location_taken (คลังในไฟล์ถูกแทนที่พื้นที่เก็บทั้งชุด จึงย้ายกันเองได้)
    postValidate: (rows, _lookupSets, lookupMaps = {}) => {
      const ownerOf = lookupMaps.storage_locations;
      const fileCodes = new Set(rows.map((r) => r.cells.warehouse_code?.raw).filter(Boolean));
      const firstRowOf = new Map(); // location code -> index of the first row that lists it
      rows.forEach((r, ri) => {
        const cell = r.cells.storage_locations;
        if (!cell || cell.error || !Array.isArray(cell.value)) return;
        const repeated = [];
        const taken = [];
        cell.value.forEach((code) => {
          if (!firstRowOf.has(code)) firstRowOf.set(code, ri);
          else if (firstRowOf.get(code) !== ri) repeated.push(code);
          const owner = ownerOf?.get(code);
          if (owner && owner !== r.cells.warehouse_code?.raw && !fileCodes.has(owner)) taken.push(`${code} → ${owner}`);
        });
        if (repeated.length) {
          cell.error = "duplicate";
          cell.detail = repeated.join(", ");
        } else if (taken.length) {
          cell.error = "location_taken";
          cell.detail = taken.join(", ");
        }
        if (cell.error) r.valid = false;
      });
      return rows;
    },
    // คลังรถช่าง (van) ผูกกับช่าง ไม่ได้อยู่ใน template นี้ -> ไม่ส่งออก (นำกลับเข้ามาก็ไม่ผ่านประเภทคลังอยู่ดี)
    flattenForExport: (data) => (data || []).filter((w) => w.warehouse_type !== "van"),
    toRow: (item, idx) => [
      idx + 1,
      item.department?.department_code || "",
      item.warehouse_code || "",
      item.name || "",
      WAREHOUSE_TYPE_LABELS[item.warehouse_type] || item.warehouse_type || "",
      item.description || "",
      (item.locations || []).map((l) => l.location_code).join(", "),
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      department_code: cells.department_code.value,
      warehouse_code: cells.warehouse_code.value,
      name: cells.name.value,
      warehouse_type: cells.warehouse_type.value,
      description: cells.description?.value || "",
      storage_locations: Array.isArray(cells.storage_locations?.value)
        ? cells.storage_locations.value
        : [],
      is_active: cells.is_active.value === true,
    }),
  },

  uom: {
    endpoint: "warehouse/uoms",
    importPath: "warehouse/uoms/import", // bulk upsert by uom_code
    templateAsset: uomTemplateFile,
    templateFile: "07_UnitOfMeasure_Template.xlsx",
    exportFile: "UnitOfMeasure_Export.xlsx",
    sheetName: "Unit Template",
    headerRows: 7,
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: UOM_COLUMNS,
    toRow: (item, idx) => [
      idx + 1,
      item.uom_code || "",
      item.name || "",
      item.symbol || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      uom_code: cells.uom_code.value,
      name: cells.name.value,
      symbol: cells.symbol?.value || "",
      description: cells.description?.value || "",
      is_active: cells.is_active.value === true,
    }),
  },

  category: {
    endpoint: "warehouse/part-categories",
    importPath: "warehouse/part-categories/import", // bulk upsert by category_code
    templateAsset: categoryTemplateFile,
    templateFile: "08_Category_Template.xlsx",
    exportFile: "Category_Export.xlsx",
    sheetName: "Category Template",
    headerRows: 8, // row 7 = group titles, row 8 = column headers -> data from row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: CATEGORY_COLUMNS,
    toRow: (item, idx) => {
      const fc = Array.isArray(item.field_control) ? item.field_control : [];
      const yn = (k) => (fc.includes(k) ? "Yes" : "No");
      return [
        idx + 1,
        item.category_code || "",
        item.name || "",
        item.description || "",
        yn("from_warehouse_id"),
        yn("location_id"),
        yn("lot_id"),
        yn("lot_received_date"),
        item.is_active ? "Active" : "Inactive",
      ];
    },
    toPayload: (cells) => {
      const field_control = [];
      if (cells.warehouse_control?.value === true) field_control.push("from_warehouse_id");
      if (cells.location_control?.value === true) field_control.push("location_id");
      if (cells.batch_control?.value === true) field_control.push("lot_id");
      if (cells.date_control?.value === true) field_control.push("lot_received_date");
      return {
        company_id: company?.id || "",
        category_code: cells.category_code.value,
        name: cells.name.value,
        description: cells.description?.value || "",
        field_control,
        is_active: cells.is_active.value === true,
      };
    },
  },

  // "แบรนด์" (Brand) — table `brands`; key/endpoint คงเดิมตาม API — Template 05.2_Brand_Template.xlsx
  brand: {
    endpoint: "warehouse/brands",
    importPath: "warehouse/brands/import", // bulk upsert by brand_code: มีแล้ว update / ไม่มีสร้างใหม่ (ไม่แตะ parent/level)
    templateAsset: brandTemplateFile,
    templateFile: "05.2_Brand_Template.xlsx",
    exportFile: "Brand_Export.xlsx",
    sheetName: "Brand Template", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 7, // header แถว 7 -> ข้อมูลเริ่มแถว 8
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: BRAND_COLUMNS,
    // Nested (tops + children) -> flat rows so ทุกระดับถูก export ครบ.
    // ไม่มีคอลัมน์ parent แล้ว — ตอน import กลับเข้ามา backend จะคง parent/level เดิมไว้.
    flattenForExport: (data) => {
      const out = [];
      (data || []).forEach((top) => {
        out.push(top);
        (top.children || []).forEach((ch) => out.push(ch));
      });
      return out;
    },
    toRow: (item, idx) => [
      idx + 1,
      item.brand_code || "",
      item.name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      brand_code: normalizeBrandText(cells.brand_code.value),
      name: normalizeBrandText(cells.name.value),
      description: String(cells.description?.value || "").trim(),
      is_active: cells.is_active.value === true,
    }),
  },

  // "รุ่น" (Model) — table `models`; key/endpoint คงเดิมตาม API — Template 03_Model_Template.xlsx (นำเข้าหลัง Brand)
  model: {
    endpoint: "warehouse/models",
    importPath: "warehouse/models/import", // สร้างใหม่เท่านั้น: แบรนด์ + รหัสรุ่นที่มีแล้ว -> error, รหัสแบรนด์ไม่มีในระบบ -> error
    templateAsset: modelTemplateFile,
    templateFile: "03_Model_Template.xlsx",
    exportFile: "Model_Export.xlsx",
    sheetName: "Model Template", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 7, // header แถว 7 -> ข้อมูลเริ่มแถว 8
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: MODEL_COLUMNS,
    // brand_code ต้องมีในระบบ (เทียบไม่สนตัวพิมพ์ เพราะรหัสแบรนด์ในระบบเป็นตัวพิมพ์ใหญ่); tree ซ้อน -> flatten
    // existing_models ไม่ใช่คอลัมน์ — เป็น set ของคู่ "รหัสแบรนด์|รหัสรุ่น" ที่มีในระบบแล้ว ใช้ใน postValidate
    lookups: {
      brand_code: {
        endpoint: "warehouse/brands",
        field: "brand_code",
        lower: true,
        flatten: (data) => (data || []).flatMap((b) => [b, ...(b.children || [])]),
      },
      existing_models: {
        endpoint: "warehouse/models",
        field: "pair",
        lower: true,
        flatten: (data) => (data || []).map((m) => ({ pair: `${normalizeBrandText(m.brand?.brand_code)}|${String(m.model_code || "").trim()}` })),
      },
    },
    // รหัสรุ่นห้ามซ้ำเฉพาะภายใต้แบรนด์เดียวกัน (แบรนด์ว่าง = กลุ่ม "ไม่มีแบรนด์") -> เช็คคู่ แบรนด์ + รหัส แบบไม่สนตัวพิมพ์
    //   ซ้ำกันเองในไฟล์ -> duplicate, มีอยู่แล้วในระบบ -> exists (backend จะ reject ทั้งไฟล์ จึงต้องดักให้เห็นตั้งแต่พรีวิว)
    postValidate: (rows, lookupSets = {}) => {
      const existing = lookupSets.existing_models;
      const seen = new Set();
      rows.forEach((r) => {
        const cell = r.cells.model_code;
        if (!cell || cell.error || !cell.raw) return;
        const brandCode = normalizeBrandText(r.cells.brand_code?.raw);
        const key = `${brandCode}|${cell.raw}`.toLowerCase();
        const label = brandCode ? `${brandCode} / ${cell.raw}` : cell.raw;
        if (seen.has(key)) {
          cell.error = "duplicate";
          cell.detail = label;
        } else if (existing?.has(key)) {
          cell.error = "exists";
          cell.detail = label;
        }
        seen.add(key);
        if (cell.error) r.valid = false;
      });
      return rows;
    },
    toRow: (item, idx) => [
      idx + 1,
      item.model_code || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.brand?.name || "",
      item.description || "",
      item.is_active ? "Active" : "Inactive",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      model_code: String(cells.model_code.value || "").trim(),
      name: String(cells.name.value || "").trim(),
      brand_code: normalizeBrandText(cells.brand_code?.value),
      description: String(cells.description?.value || "").trim(),
      is_active: cells.is_active.value === true,
    }),
  },

  part_master: {
    endpoint: "warehouse/part-masters",
    importPath: "warehouse/part-masters/import", // bulk upsert by part_number + resolve relations
    templateAsset: partTemplateFile,
    templateFile: "09_Item_Master_Template.xlsx",
    exportFile: "ItemMaster_Export.xlsx",
    sheetName: "Data Template",
    headerRows: 8, // row 7 = group titles, row 8 = column headers -> data from row 9
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: PART_COLUMNS,
    // All lookups are company-scoped. Categories share one fetch (endpoint cache).
    lookups: {
      brand_code: { endpoint: "warehouse/brands", field: "brand_code", flatten: (d) => (d || []).flatMap((b) => [b, ...(b.children || [])]) },
      model_code: { endpoint: "warehouse/models", field: "model_code" },
      serie_name: { endpoint: "warehouse/series", field: "name" },
      category_code_1: { endpoint: "warehouse/part-categories", field: "category_code" },
      category_code_2: { endpoint: "warehouse/part-categories", field: "category_code" },
      category_code_3: { endpoint: "warehouse/part-categories", field: "category_code" },
      uom_code: { endpoint: "warehouse/uoms", field: "uom_code" },
    },
    toRow: (item, idx) => [
      idx + 1,
      item.part_number || "",
      item.barcode || "",
      item.name || "",
      item.brand?.brand_code || "",
      item.brand?.name || "",
      item.model?.model_code || "",
      item.model?.name || "",
      item.serie?.name || "",
      item.serie?.name || "",
      item.part_category?.category_code || "",
      item.part_category?.name || "",
      item.part_category_2?.category_code || "",
      item.part_category_2?.name || "",
      item.part_category_3?.category_code || "",
      item.part_category_3?.name || "",
      item.uom?.uom_code || "",
      item.uom?.name || "",
      item.uom_cost ?? "",
      item.selling_price ?? "",
      item.min_stock ?? "",
      item.reorder_point ?? "",
      item.reorder_qty ?? "",
      item.lead_time_days ?? "",
      item.supplier?.name || item.supplier_name || "",
      item.legacy_part?.part_number || "",
      (item.compatible_series || []).map((s) => s.name).join(", "),
    ],
    toPayload: (cells) => {
      const num = (k) => {
        const v = cells[k]?.value;
        return v !== "" && v != null && isFinite(Number(v)) ? Number(v) : null;
      };
      const int = (k) => {
        const n = num(k);
        return n == null ? 0 : Math.trunc(n);
      };
      const money = (k) => {
        const n = num(k);
        return n == null ? null : Math.round(n * 100) / 100;
      };
      return {
        company_id: company?.id || "",
        part_number: cells.part_number.value,
        name: cells.name.value,
        description: cells.name.value, // template has a single Description field -> name + description
        barcode: cells.barcode?.value || "",
        brand_code: cells.brand_code?.value || "",
        model_code: cells.model_code?.value || "",
        serie_name: cells.serie_name?.value || "",
        category_code_1: cells.category_code_1.value,
        category_code_2: cells.category_code_2?.value || "",
        category_code_3: cells.category_code_3?.value || "",
        uom_code: cells.uom_code.value,
        uom_cost: money("uom_cost"),
        selling_price: money("selling_price"),
        min_stock: int("min_stock"),
        reorder_point: int("reorder_point"),
        reorder_qty: int("reorder_qty"),
        lead_time_days: int("lead_time_days"),
        supplier_name: cells.supplier_name?.value || "",
        is_active: cells.is_active?.value ?? true,
      };
    },
  },

  /**
   * โครงการ (Template No. 16) — ไฟล์ export รายการโครงการจาก SAP: 1 แถว = 1 โครงการ
   * upsert ด้วย project_code (มีแล้ว = update, ยังไม่มี = create) ส่วน WBS ของโครงการนำเข้าด้วยชุด "wbs"
   */
  project: {
    endpoint: "admin/projects",
    importPath: "admin/projects/import", // upsert by project_code
    // รหัสโครงการซ้ำในไฟล์ได้ (แถวหลังทับแถวก่อน) - ถ้ายิงหลาย chunk พร้อมกัน แถวซ้ำที่อยู่คนละ chunk
    // จะแย่งกันสร้างโครงการเดียวกัน (unique company_id + project_code) จึงต้องส่งทีละ chunk ตามลำดับไฟล์
    sequentialImport: true,
    templateAsset: projectTemplateFile,
    templateFile: "16_project_Template.xlsx",
    exportFile: "Project_Export.xlsx",
    sheetName: "SAPUI5 Export", // ชื่อ sheet ของไฟล์ที่ SAP export ออกมา ห้ามเปลี่ยน
    headerRows: 1, // row 1 is the column header; data starts at row 2
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: PROJECT_COLUMNS,
    toRow: (item) => [
      item.project_code || "",
      item.name || "",
      item.owner_code || "",
      item.owner_name || "",
      toSapDate(item.planned_start_date),
      toSapDate(item.planned_finish_date),
      toSapDate(item.actual_start_date),
      toSapDate(item.actual_finish_date),
      item.processing_status || "",
      item.project_manager || "",
      item.sap_created_by || "",
      item.description || "",
      toSapDateTime(item.sap_created_on),
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      project_code: cells.project_code.value,
      name: cells.name.value,
      owner_code: cells.owner_code?.value || "",
      owner_name: cells.owner_name?.value || "",
      // validateCell แปลงเป็น dd/mm/yyyy (date) และ ISO UTC (datetime) มาแล้ว - backend รับได้ทั้งคู่
      planned_start_date: cells.planned_start_date?.value || "",
      planned_finish_date: cells.planned_finish_date?.value || "",
      actual_start_date: cells.actual_start_date?.value || "",
      actual_finish_date: cells.actual_finish_date?.value || "",
      processing_status: cells.processing_status?.value || "",
      project_manager: cells.project_manager?.value || "",
      sap_created_by: cells.sap_created_by?.value || "",
      description: cells.description?.value || "",
      sap_created_on: cells.sap_created_on?.value || "",
    }),
  },

  wbs: {
    endpoint: "admin/wbs", // 1 แถว = 1 WBS (คืน project_code / name ของโครงการแบบแบนมาด้วย)
    importPath: "admin/wbs/import", // upsert project by project_code -> upsert wbs by wbs_element
    // แถวของโครงการเดียวกันกระจายอยู่ได้หลาย chunk - ถ้ายิงพร้อมกัน chunk ที่เจอโครงการใหม่ตัวเดียวกัน
    // จะสร้างโครงการซ้ำชนกัน (unique company_id + project_code) จึงต้องส่งทีละ chunk
    sequentialImport: true,
    templateAsset: wbsTemplateFile,
    templateFile: "13_wbs_Template.xlsx",
    exportFile: "WBS_Export.xlsx",
    sheetName: "project", // ชื่อ sheet ในไฟล์ template ห้ามเปลี่ยน
    headerRows: 1, // row 1 is the column header; data starts at row 2
    readByHeader: true, // จับคอลัมน์ตามชื่อหัว ไม่ผูกตำแหน่ง (ดู alignRowsByHeader)
    columns: WBS_COLUMNS,
    toRow: (item) => [
      item.entity_guid || "",
      item.project_code || "",
      item.name || "",
      item.wbs_element || "",
      item.wbs_element_name || "",
    ],
    toPayload: (cells) => ({
      company_id: company?.id || "",
      entity_guid: cells.entity_guid?.value || "",
      project_code: cells.project_code.value,
      name: cells.name.value,
      wbs_element: cells.wbs_element?.value || "",
      wbs_element_name: cells.wbs_element_name?.value || "",
    }),
  },

  /**
   * สต็อกคงเหลือ (Template No. 14) — ไฟล์ export จาก SAP: หัวคอลัมน์แถว 1 ข้อมูลเริ่มแถว 2
   * ต่างจากชุดอื่นตรงที่ไม่ได้ยิง rows เข้า endpoint เดียว แต่นำเข้าแบบ full-replace ผ่าน batch
   * (เปิดรอบ -> ส่งทีละ chunk -> commit) จึงใช้ parseFile/runImport ของ utils/stockImport แทน
   * pipeline กลาง ส่วนหน้าจอ preview/progress ใช้ร่วมกับชุดอื่นตามปกติ
   */
  stock: {
    endpoint: "warehouse/stocks/export", // คืนค่าเป็นแถวแบนรูปแบบเดียวกับไฟล์นำเข้า (1 แถว = 1 batch)
    importPath: "warehouse/stocks/import/batches",
    templateAsset: inventoryTemplateFile,
    templateFile: "14_Inventory_Template.xlsx",
    exportFile: "Inventory_Export.xlsx",
    sheetName: STOCK_IMPORT_SHEET,
    headerRows: STOCK_IMPORT_HEADER_ROWS,
    columns: STOCK_IMPORT_COLUMNS,
    // ไฟล์นี้ไม่มีคอลัมน์ UID ในเทมเพลต -> ส่งออกให้เหมือนเทมเพลตเป๊ะ ๆ เพื่อให้นำกลับเข้ามาได้ทันที
    noUidColumn: true,
    // ลบสต็อกเดิมทั้งบริษัทแล้วสร้างใหม่ -> ต้องเตือนให้ชัดกว่าชุดข้อมูลอื่น
    confirmDetail: "confirm_import_replace_all_detail",
    parseFile: async (file) => (await parseStockWorkbook(file)).rows,
    runImport: ({ rows, fileName, onProgress }) =>
      runStockImport({ rows, fileName, sheetName: STOCK_IMPORT_SHEET, onProgress }),
    summaryText: (summary) => stockImportSummaryText(summary),
    toRow: (item) => [
      item.material_code || "",
      item.material_name || "",
      item.plant_code || "",
      item.plant_name || "",
      item.storage_location_code || "",
      item.storage_location_name || "",
      item.special_stock_type || "",
      item.special_stock_type_name || "",
      item.wbs_element || "",
      item.wbs_element_name || "",
      item.batch_number || "",
      Number(item.unrestricted_qty || 0),
      Number(item.quality_inspection_qty || 0),
      Number(item.blocked_qty || 0),
    ],
  },

};

/**
 * Rows are POSTed to the import endpoint in chunks so the UI can show real
 * completion %. To keep it fast, chunks are sent with bounded concurrency
 * (IMPORT_CONCURRENCY at a time) instead of strictly one-after-another.
 * Each chunk is its own backend transaction; the whole-file client validation
 * (unique codes) keeps disjoint chunks conflict-free. Data sets with intra-file
 * dependencies set `sequentialImport: true` and provide `sortRowsForImport`
 * so the rows they depend on are always committed first.
 */
const IMPORT_CHUNK_SIZE = 100;
const IMPORT_CONCURRENCY = 4;

export default class ImportCenter extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      filter_search: "",

      // Import preview modal
      import_modal: false,
      active_key: "",
      import_rows: [], // [{ cells: { key: { raw, value, error } }, valid }]
      import_file_name: "", // ชื่อไฟล์ที่เลือก (ชุดข้อมูลที่นำเข้าแบบ batch เก็บไว้ในรอบนำเข้า)
      importing: false, // true while import requests are in flight
      import_progress: 0, // 0-100 completion % across all chunks
      import_done: 0, // rows committed so far (for the "x / total" hint)
      show_invalid_only: false, // preview table: filter to error rows only

      // ตัวเลือกที่ต้องเลือกก่อนยืนยัน แล้วใช้กับทุกแถวในไฟล์ (cfg.bulkOption — ตอนนี้มีแค่ชุดข้อมูลช่าง)
      bulk_options: [], // รายการตัวเลือกที่ดึงมาจาก cfg.bulkOption.endpoint
      bulk_value: "",   // ค่าที่ผู้ใช้เลือก — ว่าง = ยังยืนยันนำเข้าไม่ได้

      // Data set catalog (UI list)
      data_sets: [
        { key: "customer", name: "import_customer", description: "import_customer_desc", template: "01_Customer_Template.xlsx", icon: "", color: "primary" },
        { key: "technician", name: "import_technician", description: "import_technician_desc", template: "15_Technician_Template.xlsx", icon: "", color: "primary" },
        { key: "site", name: "import_site", description: "import_site_desc", template: "17_Site_Template.xlsx", icon: "", color: "primary" },
        { key: "section", name: "import_section", description: "import_section_desc", template: "18_Department_Template.xlsx", icon: "", color: "primary" },
        { key: "part_master", name: "import_part_master", description: "import_part_master_desc", template: "09_Item_Master_Template.xlsx", icon: "", color: "primary" },
        { key: "asset", name: "import_asset", description: "import_asset_desc", template: "10_Asset_Template.xlsx", icon: "", color: "primary" },
        { key: "machine", name: "import_machine", description: "import_machine_desc", template: "19_Machine_Equipment.xlsx", icon: "", color: "primary" },
        { key: "location", name: "import_location", description: "import_location_desc", template: "12_Location_Template.xlsx", icon: "", color: "primary" },
        { key: "warehouse", name: "import_warehouse", description: "import_warehouse_desc", template: "11_Warehouse_Template.xlsx", icon: "", color: "primary" },
        { key: "uom", name: "import_uom", description: "import_uom_desc", template: "07_UnitOfMeasure_Template.xlsx", icon: "", color: "primary" },
        { key: "category", name: "import_category", description: "import_category_desc", template: "08_Category_Template.xlsx", icon: "", color: "primary" },
        // key ของ dataset คงเดิม — เปลี่ยนเฉพาะชื่อที่แสดงผล (ดู glossary ใน components/global.js)
        // brand = แบรนด์ (table brands, Template 05.2) -> model = รุ่น (table models, Template 03, ต้องนำเข้าหลัง brand)
        { key: "brand", name: "import_material_type", description: "import_material_type_desc", template: "05.2_Brand_Template.xlsx", icon: "", color: "primary" },
        // รุ่น (Model, table models, Template 03) — นำเข้าหลัง brand: รหัสแบรนด์ต้องมีในระบบ, แบรนด์ + รหัสรุ่นที่มีแล้ว = error
        { key: "model", name: "import_material_group", description: "import_material_group_desc", template: "03_Model_Template.xlsx", icon: "\uf1b2", color: "primary" },
        // ปิดชุดนำเข้าโครงการไว้ก่อน (ยังไม่ได้ใช้งาน) - config ของ dataset "project" ยังอยู่ เปิดกลับได้ทันที
        // { key: "project", name: "import_project", description: "import_project_desc", template: "16_project_Template.xlsx", icon: "", color: "primary" },
        // ปิดชุดนำเข้า WBS ไว้ก่อน (ยังไม่ได้ใช้งาน พร้อมกับโครงการ) - config ของ dataset "wbs" ยังอยู่
        // { key: "wbs", name: "import_wbs", description: "import_wbs_desc", template: "13_wbs_Template.xlsx", icon: "", color: "primary" },
        { key: "stock", name: "import_stock", description: "import_stock_desc", template: "14_Inventory_Template.xlsx", icon: "", color: "primary" },
      ],
    };
  }

  async componentDidMount() {
    loading(false);
  }

  // ---- Helpers -------------------------------------------------------------

  // Clear any leftover import preview / state. Called at the start of every action.
  resetImport = () => {
    this.setState({ import_modal: false, active_key: "", import_rows: [], import_file_name: "", importing: false, import_progress: 0, import_done: 0, show_invalid_only: false, bulk_options: [], bulk_value: "" });
  };


  // Header row of a workbook for this data set (0-based row cfg.headerRows - 1).
  headerRowOf = (wb, cfg) => {
    const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];
    if (!ws) return [];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", blankrows: true });
    return rows[cfg.headerRows - 1] || [];
  };

  /**
   * จัดคอลัมน์ของแถวข้อมูลใหม่ให้เรียงตาม cfg.columns โดยจับคู่จากชื่อหัวคอลัมน์ (en / th / aliases)
   * ไม่สนตัวพิมพ์ ช่องว่าง การขึ้นบรรทัด · คอลัมน์เกินในไฟล์ (เช่น UID ท้ายไฟล์ส่งออก) ถูกข้าม · หัวที่ไม่พบ = ค่าว่าง
   * "แผนก" ใน template เก่าถือเป็น "หน่วยงาน" (แปลงด้วย dept ด้านล่าง) · aliases เฉพาะ dataset ตั้งที่ column.aliases
   * @returns {{ rows: any[][], missing: string[] }} missing = ชื่อคอลัมน์บังคับที่หาไม่เจอในไฟล์
   */
  alignRowsByHeader = (cfg, wb, raw) => {
    const dept = (v) => (typeof v === "string" ? v.replace(/แผนก(?!าร)/g, "หน่วยงาน") : v);
    const headerRow = this.headerRowOf(wb, cfg).map(dept);
    const columns = cfg.columns.map((c) => ({ ...c, en: dept(c.en), th: dept(c.th), aliases: (c.aliases || []).map(dept) }));
    const { colIndex, missing } = matchColumnsByHeader(headerRow, columns);
    const rows = raw.map((arr) => colIndex.map((i) => (i < 0 ? "" : arr[i])));
    return { rows, missing };
  };

  // Read the "Reference" sheet of the uploaded file -> { columnKey: [allowed labels] }
  parseReference = (wb, cfg) => {
    const refMap = {};
    if (!cfg.reference) return refMap;

    const sheetName = wb.SheetNames.find((n) => n.includes(cfg.reference.sheetMatch));
    if (!sheetName) return refMap;

    // Section titles always live in column A; the allowed value may live in a
    // later column (e.g. customer Reference has "No." in A and the value in B).
    const valueCol = cfg.reference.valueColumn || 0;
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
    let current = null;
    rows.forEach((row) => {
      const head = String(row[0] ?? "").trim();
      // A "N. Title" line (or one of our section titles) is a section boundary.
      const isSectionHeader = /^\d+\s*\./.test(head) || cfg.reference.sections.some((s) => head.includes(s.startsWith));
      if (isSectionHeader) {
        const sec = cfg.reference.sections.find((s) => head.includes(s.startsWith));
        current = sec ? sec.columnKey : null; // unknown section -> stop collecting
        if (current) refMap[current] = [];
        return;
      }
      if (!current) return;
      const val = String(row[valueCol] ?? "").trim();
      if (!val) return;
      // skip the "ค่า (Value)" column-header row
      if (val.includes("ค่า") || val.toLowerCase().includes("value")) return;
      refMap[current].push(val);
    });
    return refMap;
  };

  // Derive the stored value for an enum reference label.
  //   default      "โซน (Zone)" -> "zone"   (extract the english key in parentheses)
  //   mode "label" "BPSB"       -> "BPSB"   (keep the value verbatim, e.g. a branch code)
  enumKeyOf = (col, label) => {
    if (col && col.enumKeyMode === "label") return String(label ?? "").trim();
    const m = String(label ?? "").match(/\(([^)]+)\)/);
    return (m ? m[1] : String(label ?? "")).trim().toLowerCase();
  };

  // Candidate {key,label} list for an enum column: prefer the Reference sheet, fall back to config options.
  enumCandidates = (col, refMap) => {
    // Fixed label -> key table (e.g. "คลังหลัก" -> "main"); labels without an english key in parentheses.
    if (col.enumAliases) {
      return Object.entries(col.enumAliases).flatMap(([key, labels]) => labels.map((label) => ({ key, label })));
    }
    const refValues = refMap && refMap[col.key];
    if (refValues && refValues.length) {
      return refValues.map((label) => ({ key: this.enumKeyOf(col, label), label }));
    }
    return (col.options || []).map((o) => ({ key: o, label: t(o) }));
  };

  // Match a cell value against the candidates by exact key or label (case-insensitive).
  matchEnum = (val, candidates) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (!v) return null;
    for (const c of candidates) {
      const k = String(c.key).trim().toLowerCase();
      const l = String(c.label).trim().toLowerCase();
      if (v === k || v === l) return c.key;
    }
    return null;
  };

  normStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["เปิดใช้งาน", "ใช้งาน", "active", "true", "1"].includes(v)) return true;
    if (["ปิดใช้งาน", "ไม่ใช้งาน", "inactive", "false", "0"].includes(v)) return false;
    return null;
  };

  // Customer status is tri-state -> canonical "Active" | "Inactive" | "Hold" (null = invalid).
  normCustomerStatus = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (["active", "เปิดใช้งาน", "ใช้งาน", "true", "1"].includes(v)) return "Active";
    if (["inactive", "ปิดใช้งาน", "ไม่ใช้งาน", "false", "0"].includes(v)) return "Inactive";
    if (["hold", "on hold", "ระงับ", "พักใช้งาน", "พัก"].includes(v)) return "Hold";
    return null;
  };

  // Yes/No -> boolean. Empty defaults to false (No). Returns null for invalid values.
  normBool = (val) => {
    const v = String(val ?? "").trim().toLowerCase();
    if (v === "") return false;
    if (["yes", "y", "true", "1", "ใช่"].includes(v)) return true;
    if (["no", "n", "false", "0", "ไม่", "ไม่ใช่"].includes(v)) return false;
    return null;
  };

  // Basic email shape check (value is already trimmed by the caller).
  isEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val ?? "").trim());

  // Excel เก็บวันที่เป็นเลขลำดับวัน (serial) นับจาก 30/12/1899 — ไฟล์ที่ export ออกมาจาก Excel
  // ส่งค่าพวกนี้มาเป็นตัวเลขล้วน ต้องแปลงเป็น dd/mm/yyyy ก่อน ไม่งั้นพรีวิวจะโชว์ "44525"
  // และหลังบ้านก็อ่านไม่ออก (1 = 31/12/1899, 2958465 = 31/12/9999)
  excelSerialToDate = (serial) => {
    const n = Number(serial);
    if (!Number.isFinite(n) || n < 1 || n > 2958465) return null;
    const ms = Date.UTC(1899, 11, 30) + Math.floor(n) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  };

  // Accept DD/MM/YYYY (incl. Buddhist year), an Excel date serial, or anything Date can parse;
  // returns the value to store (dd/mm/yyyy for serials, verbatim otherwise) or null when invalid.
  normDate = (val) => {
    let s = String(val ?? "").trim();
    if (!s) return null;
    // dd.mm.yyyy (รูปแบบวันที่ของ SAP) -> dd/mm/yyyy ต้องแปลงก่อนถึง new Date() ด้านล่าง
    // ไม่งั้น "01.09.2025" จะถูกอ่านแบบอเมริกันเป็น 9 ม.ค. แล้วผ่านการตรวจไปทั้งที่ผิดวัน
    const dotted = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotted) s = `${dotted[1].padStart(2, "0")}/${dotted[2].padStart(2, "0")}/${dotted[3]}`;
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo] = m;
      const day = Number(d), month = Number(mo);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return s;
    }
    if (/^\d+(\.\d+)?$/.test(s)) return this.excelSerialToDate(s);
    return Number.isNaN(new Date(s).getTime()) ? null : s;
  };

  /**
   * วันเวลา (UTC) -> { value: ISO string ที่ส่งเข้า backend, display: "dd.mm.yyyy, HH:MM:SS" } หรือ null ถ้าอ่านไม่ออก
   * รับ Excel serial (เศษทศนิยม = เวลา), "dd.mm.yyyy, HH:MM[:SS]" (คั่นวันที่ด้วย . / - ได้) และ ISO
   * ค่าในไฟล์ถือเป็น UTC ตามหัวคอลัมน์ "Created On (UTC)" จึงไม่แปลงตาม timezone ของเครื่องผู้ใช้
   */
  normDateTime = (val) => {
    const s = String(val ?? "").trim();
    if (!s) return null;
    const pad = (n) => String(n).padStart(2, "0");
    let ms = null;

    const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[,\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (m) {
      let year = Number(m[3]);
      if (year > 2400) year -= 543; // พ.ศ. -> ค.ศ.
      const [day, month, hh, mm, ss] = [Number(m[1]), Number(m[2]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)];
      if (month < 1 || month > 12 || day < 1 || day > 31 || hh > 23 || mm > 59 || ss > 59) return null;
      ms = Date.UTC(year, month - 1, day, hh, mm, ss);
      if (new Date(ms).getUTCDate() !== day) return null; // กันวันที่ไม่มีจริง เช่น 31/02
    } else if (/^\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (n < 1 || n > 2958465) return null;
      ms = Date.UTC(1899, 11, 30) + Math.round(n * 86400000);
    } else if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) {
      const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
      const iso = s.includes("T") || s.includes(" ") ? s.replace(" ", "T") : `${s}T00:00:00`;
      ms = new Date(hasZone ? iso : `${iso}Z`).getTime();
    }

    if (ms === null || Number.isNaN(ms)) return null;
    const d = new Date(ms);
    return {
      value: d.toISOString(),
      display: `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`,
    };
  };

  validateCell = (col, raw, seen, refMap = {}, lookupSets = {}, lookupLower = {}) => {
    const s = raw === undefined || raw === null ? "" : String(raw).trim();
    // ค่าที่เอาไปเทียบกับ lookup set ต้องแปลงเคสให้ตรงกับตอนสร้าง set
    const lookupKey = (v) => (lookupLower[col.key] ? String(v).toLowerCase() : String(v));
    let value = s;
    let error = "";
    let detail = "";
    let display; // ค่าที่ให้พรีวิวแสดงแทน raw (ปล่อย undefined = โชว์ raw ตามเดิม)

    if (col.type === "number") {
      if (s !== "" && !isFinite(Number(s))) error = "number";
    } else if (col.type === "email") {
      if (s === "") {
        if (col.required) error = "required";
      } else if (!this.isEmail(s)) {
        error = "email";
      }
    } else if (col.type === "date") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const d = this.normDate(s);
        // serial ของ Excel ถูกแปลงเป็น dd/mm/yyyy แล้ว -> ให้ทั้งพรีวิวและ payload เห็นค่าที่อ่านออก
        if (d === null) error = "date";
        else { value = d; display = d; }
      }
    } else if (col.type === "datetime") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const d = this.normDateTime(s);
        if (d === null) error = "date";
        else { value = d.value; display = d.display; }
      }
    } else if (col.type === "enum") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const n = this.matchEnum(s, this.enumCandidates(col, refMap));
        if (!n) error = "enum";
        else value = n;
      }
    } else if (col.type === "status") {
      if (s === "") {
        if (col.required) error = "required";
      } else {
        const b = this.normStatus(s);
        if (b === null) error = "status";
        else value = b;
      }
    } else if (col.type === "customer_status") {
      if (s === "") {
        if (col.required) error = "required";
        else value = "Active"; // default missing status to Active
      } else {
        const cs = this.normCustomerStatus(s);
        if (cs === null) error = "customer_status";
        else value = cs; // "Active" | "Inactive" | "Hold"
      }
    } else if (col.type === "bool") {
      // Yes/No flag, optional, defaults to No.
      const b = this.normBool(s);
      if (b === null) error = "bool";
      else value = b;
    } else if (col.type === "list") {
      // Comma-separated codes (a line break inside the cell also separates, surrounding blanks are dropped);
      // optionally checked against a lookup set (e.g. company locations).
      if (s === "") {
        if (col.required) error = "required";
        value = [];
      } else {
        const items = [...new Set(s.split(/[,\r\n]+/).map((x) => x.trim()).filter(Boolean))];
        value = items;
        const set = lookupSets[col.key];
        if (set) {
          const missing = items.filter((it) => !set.has(lookupKey(it)));
          if (missing.length) {
            error = "lookup";
            detail = missing.join(", ");
          }
        }
      }
    } else {
      // text — optionally checked against a single-value lookup set (e.g. brand_code).
      if (col.required && s === "") {
        error = "required";
      } else if (s !== "" && lookupSets[col.key] && !lookupSets[col.key].has(lookupKey(s))) {
        error = "lookup";
        detail = s;
      }
    }

    if (!error && col.unique && s !== "") {
      const k = s.toLowerCase();
      if (seen[k]) error = "duplicate";
      else seen[k] = true;
    }

    // ความยาวเกินที่ backend รับ (DTO ฝั่ง API) — เช็คหลังเช็คชนิดข้อมูลผ่านแล้ว detail = "ยาวจริง/สูงสุด"
    if (!error && col.maxLength && s.length > col.maxLength) {
      error = "too_long";
      detail = `${s.length}/${col.maxLength}`;
    }

    return { raw: s, value, error, detail, ...(display !== undefined && { display }) };
  };

  validateRows = (cfg, raw, refMap = {}, lookupSets = {}, lookupLower = {}) => {
    const seen = {};
    const out = [];
    raw.forEach((arr, rawIdx) => {
      // Drop rows with no real data. Ignore the auto running-number column and
      // bool columns left at their default "No" (templates pre-fill these).
      const allEmpty = cfg.columns.every((c, ci) => {
        if (c.type === "auto") return true;
        if (cfg.prefilledKeys?.includes(c.key)) return true; // template pre-fills this column on every row
        const v = String(arr[ci] ?? "").trim();
        if (v === "") return true;
        if (c.type === "bool" && this.normBool(v) === false) return true;
        if (c.type === "number" && Number(v) === 0) return true; // templates pre-fill 0
        return false;
      });
      if (allEmpty) return;

      const cells = {};
      let valid = true;
      cfg.columns.forEach((c, ci) => {
        if (c.type === "auto") return;
        const res = this.validateCell(c, arr[ci], seen, refMap, lookupSets, lookupLower);
        cells[c.key] = res;
        if (res.error) valid = false;
      });
      out.push({ cells, valid, excel_row: (cfg.headerRows || 0) + rawIdx + 1 }); // เลขแถวในไฟล์ Excel (1-based)
    });
    return out;
  };

  // ข้อความ error ใช้ชุดเดียวกับหน้าตรวจข้อมูล (components/ImportPreviewModal.js)
  errorText = importErrorText;

  // Fetch the company-scoped lookups declared in cfg.lookups / cfg.exportLookups ->
  //   { sets: { columnKey: Set(codes) }, maps: { columnKey: Map(code -> mapField) },
  //     lower: { columnKey: true } }.
  // `maps` is only built for lookups that declare a `mapField` (e.g. uid -> model_code on export).
  // `lower` travels with the set so validateCell knows whether to compare case-insensitively —
  // it must match how the backend resolves the same value, otherwise the preview rejects rows
  // the import would have accepted (or the other way round).
  fetchLookupSets = async (lookups) => {
    const sets = {};
    const maps = {};
    const lower = {};
    if (!lookups) return { sets, maps, lower };
    const cache = {}; // endpoint -> data[] | null (so categories aren't fetched 3x)
    for (const [colKey, lk] of Object.entries(lookups)) {
      if (!(lk.endpoint in cache)) {
        const res = await GET(lk.endpoint, { page_size: 100000, page: 0 });
        cache[lk.endpoint] = res?.status ? res.data || [] : null;
      }
      const raw = cache[lk.endpoint];
      if (raw == null) continue; // fetch failed -> skip (avoids false errors)
      const flat = lk.flatten ? lk.flatten(raw) : raw; // e.g. unfold nested Material Type tree
      const data = lk.filter ? flat.filter(lk.filter) : flat;
      const values = data.map((d) => String(d[lk.field] ?? "").trim()).filter(Boolean);
      // `lower` -> case-insensitive; default exact (codes / identifiers).
      sets[colKey] = new Set(lk.lower ? values.map((v) => v.toLowerCase()) : values);
      if (lk.lower) lower[colKey] = true;
      if (lk.mapField) {
        const m = new Map();
        data.forEach((d) => {
          const key = String(d[lk.field] ?? "").trim();
          const mapped = typeof lk.mapField === "function" ? lk.mapField(d) : d[lk.mapField];
          if (key) m.set(lk.lower ? key.toLowerCase() : key, String(mapped ?? "").trim());
        });
        maps[colKey] = m;
      }
    }
    return { sets, maps, lower };
  };

  // Options for cfg.bulkOption (company-scoped, e.g. the roles a technician can be given).
  // A failed fetch returns [] -> the select stays empty and the confirm button stays disabled.
  fetchBulkOptions = async (cfg) => {
    if (!cfg.bulkOption) return [];
    const res = await GET(cfg.bulkOption.endpoint, { page_size: 100000, page: 0 });
    return res?.status ? res.data || [] : [];
  };

  // ---- Actions -------------------------------------------------------------

  handleDownloadTemplate = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    loading(true);
    try {
      const res = await fetch(cfg.templateAsset);
      const blob = await res.blob();
      saveAs(blob, cfg.templateFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleExport = async (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }

    loading(true);
    const result = await GET(cfg.endpoint, { page_size: 100000, page: 0 });
    const data = result?.status ? result.data || [] : [];

    if (!data.length) {
      loading(false);
      alert("", "info", t("import_center"), t("export_no_data"));
      return;
    }

    try {
      // Build the export on top of the template so the layout matches the template.
      const res = await fetch(cfg.templateAsset);
      const ab = await res.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];

      // Some data sets are nested (e.g. Material Types with children) -> flatten for a tabular export.
      const exportData = cfg.flattenForExport ? cfg.flattenForExport(data) : data;

      // Export-only lookups turn stored ids back into the codes the template expects
      // (e.g. technician skills store a Material Group uid -> written out as its code).
      const { maps: exportMaps } = await this.fetchLookupSets(cfg.exportLookups);

      const headerRowIdx = cfg.headerRows - 1; // 0-based row of the column headers
      const firstDataRow = cfg.headerRows;     // 0-based row of the first data row
      // UID is appended right after the last template column, except for data sets whose
      // file must stay byte-for-byte the template layout (`noUidColumn`).
      const withUid = !cfg.noUidColumn;
      const uidCol = cfg.columns.length;
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = withUid ? Math.max(range.e.c, uidCol) : Math.max(range.e.c, cfg.columns.length - 1);

      // 1) Clear the data area. Templates ship with placeholder rows (running numbers,
      //    "No" defaults, zeros) that would otherwise leak below the real data.
      for (let r = firstDataRow; r <= range.e.r; r++) {
        for (let c = 0; c <= lastCol; c++) {
          delete ws[XLSX.utils.encode_cell({ r, c })];
        }
      }

      // 2) Append a UID column header at the end of the header row.
      if (withUid) XLSX.utils.sheet_add_aoa(ws, [["UID"]], { origin: { r: headerRowIdx, c: uidCol } });

      // 3) Write the real company data; each row carries its own uid at the very end.
      exportData.forEach((row, i) => {
        const arr = cfg.toRow(row, i, { maps: exportMaps });
        if (withUid) arr[uidCol] = row.uid || ""; // uid lands in the column after the last template column
        XLSX.utils.sheet_add_aoa(ws, [arr], { origin: { r: firstDataRow + i, c: 0 } });
      });

      // 4) Grow the sheet range to cover the written data + the UID column.
      range.e.c = lastCol;
      range.e.r = Math.max(headerRowIdx, firstDataRow + exportData.length - 1);
      ws["!ref"] = XLSX.utils.encode_range(range);

      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([out], { type: "application/octet-stream" }), cfg.exportFile);
    } catch (e) {
      alert("", "warning", t("warning"), t("download_failed"));
    }
    loading(false);
  };

  handleImport = (item) => {
    this.resetImport();
    const cfg = DATASET_CONFIG[item.key];
    if (!cfg) {
      alert("", "info", t("import_center"), t("import_not_available"));
      return;
    }
    this.setState({ active_key: item.key }, () => {
      if (this.fileInputRef.current) this.fileInputRef.current.click();
    });
  };

  handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const cfg = DATASET_CONFIG[this.state.active_key];
    if (!cfg) return;

    loading(true);
    try {
      let rows;
      if (cfg.parseFile) {
        // ชุดข้อมูลที่มีตัวอ่าน/ตรวจไฟล์ของตัวเอง (สต็อก) — คืนค่ารูปแบบเดียวกับ validateRows
        rows = await cfg.parseFile(file);
      } else {
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });
        const ws = wb.Sheets[cfg.sheetName] || wb.Sheets[wb.SheetNames[0]];

        // อ่านตามชื่อหัวคอลัมน์ (ไม่ผูกตำแหน่ง) ตรวจแค่ว่ามีคอลัมน์บังคับครบ
        let raw = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          range: cfg.headerRows, // start reading at the first data row
          defval: "",
        });
        const aligned = this.alignRowsByHeader(cfg, wb, raw);
        if (aligned.missing.length) {
          loading(false);
          alert("", "warning", t("warning"), `${t("import_required_column_missing")}: ${aligned.missing.join(", ")}`);
          return;
        }
        raw = aligned.rows;
        const refMap = this.parseReference(wb, cfg);
        const { sets: lookupSets, maps: lookupMaps, lower: lookupLower } = await this.fetchLookupSets(cfg.lookups);
        rows = this.validateRows(cfg, raw, refMap, lookupSets, lookupLower);
        if (cfg.postValidate) rows = cfg.postValidate(rows, lookupSets, lookupMaps);
        if (cfg.enrichRows) rows = cfg.enrichRows(rows, { maps: lookupMaps });
      }

      // ตัวเลือกที่ต้องเลือกก่อนยืนยัน (เช่น บทบาทของช่าง) — ดึงมาพร้อมพรีวิว
      const bulkOptions = await this.fetchBulkOptions(cfg);

      if (!rows.length) {
        loading(false);
        alert("", "info", t("import_center"), t("no_rows_found"));
        return;
      }
      // Keep the overlay up until the modal has actually rendered, otherwise the
      // loader closes a frame before the modal appears and the screen flickers.
      this.setState(
        {
          import_modal: true,
          import_rows: rows,
          import_file_name: file.name || "",
          show_invalid_only: false,
          bulk_options: bulkOptions,
          bulk_value: "",
        },
        () => loading(false)
      );
    } catch (err) {
      loading(false);
      const missingColumns = err && err.missingColumns;
      alert("", "warning", t("warning"), missingColumns ? `${t("import_required_column_missing")}: ${missingColumns.join(", ")}` : t("import_read_failed"));
    }
  };

  /**
   * นำเข้าด้วยขั้นตอนเฉพาะของชุดข้อมูล (ตอนนี้มีแค่สต็อก)
   * ตัวนำเข้าเป็นคนคุมลำดับ/ความปลอดภัยของข้อมูลเอง หน้านี้ทำแค่ยืนยัน แสดง progress และสรุปผล
   */
  runCustomImport = async (cfg, rows, fileName) => {
    if (
      !(await alert("", "warning", t("confirm"), t(cfg.confirmDetail || "confirm_import_detail"), t("confirm"), t("cancel")))
    ) {
      return;
    }

    this.setState({ importing: true, import_progress: 0, import_done: 0 });

    const result = await cfg.runImport({
      rows,
      fileName,
      onProgress: (percent, done) => this.setState({ import_progress: percent, import_done: done }),
    });

    if (!result?.ok) {
      this.setState({ importing: false, import_progress: 0, import_done: 0 });
      const detail = result?.errors?.length ? `\n${result.errors.slice(0, 10).join("\n")}` : "";
      alert("", "warning", t("warning"), `${result?.message || t("import_failed")}${detail}`);
      return;
    }

    this.setState({ importing: false });
    this.resetImport();
    alert(
      "",
      "success",
      t("import_done"),
      cfg.summaryText ? cfg.summaryText(result.summary || {}) : t("import_done")
    );
  };

  handleConfirmImport = async () => {
    const { active_key, import_rows, import_file_name } = this.state;
    const cfg = DATASET_CONFIG[active_key];
    if (!cfg) return;
    if (!import_rows.length || import_rows.some((r) => !r.valid)) return;
    // ตัวเลือกที่ใช้กับทุกแถว (เช่น บทบาท) ต้องถูกเลือกก่อน ปุ่มยืนยันถูก disable ไว้อยู่แล้ว
    // แต่กันไว้อีกชั้นเผื่อถูกเรียกจากทางอื่น
    if (cfg.bulkOption && !this.state.bulk_value) return;

    // ชุดข้อมูลที่มีขั้นตอนนำเข้าเฉพาะของตัวเอง (สต็อก = full-replace ผ่าน batch)
    if (cfg.runImport) {
      await this.runCustomImport(cfg, import_rows, import_file_name);
      return;
    }

    // No bulk upload endpoint yet for this data set -> validated/previewed only.
    if (!cfg.importPath) {
      alert("", "info", t("import_center"), t("import_pending_backend"));
      return;
    }

    if (
      !(await alert("", "info", t("confirm"), t("confirm_import_detail"), t("confirm"), t("cancel")))
    ) {
      return;
    }

    // Strip per-row company_id — it is sent once at the top level of the body.
    let rows = import_rows.map((r) => {
      const { company_id, ...rest } = cfg.toPayload(r.cells); // eslint-disable-line no-unused-vars
      return rest;
    });
    // Order rows where chunk boundaries matter (rows a later row depends on go first).
    if (cfg.sortRowsForImport) rows = cfg.sortRowsForImport(rows);

    const total = rows.length;
    // payload -> เลขแถวในไฟล์ Excel (ใช้บอกแถวที่ backend ปฏิเสธ) ผูกกับ object จึงไม่เพี้ยนแม้ sortRowsForImport สลับลำดับ
    const excelRowOf = new Map();
    if (!cfg.sortRowsForImport) rows.forEach((r, i) => excelRowOf.set(r, import_rows[i]?.excel_row));
    const chunks = [];
    for (let i = 0; i < total; i += IMPORT_CHUNK_SIZE) chunks.push(rows.slice(i, i + IMPORT_CHUNK_SIZE));

    // Drive an in-modal progress bar; the static-backdrop modal + disabled buttons
    // block every other action while it runs.
    this.setState({ importing: true, import_progress: 0, import_done: 0 });

    let created = 0;
    let updated = 0;
    let done = 0;
    let failMessage = "";
    let aborted = false;

    // ค่าที่เลือกไว้ใน modal ส่งไปครั้งเดียวต่อ chunk ที่ระดับ body ไม่ต้องยัดซ้ำทุกแถว
    const extraBody = cfg.bulkOption ? { [cfg.bulkOption.bodyField]: this.state.bulk_value } : {};

    const sendChunk = async (chunk) => {
      if (aborted) return;
      // skipErrorAlert: ให้ alert สรุปท้ายฟังก์ชัน (มีรายละเอียดรายแถว) เป็นตัวเดียวที่เด้ง
      const res = await POST(cfg.importPath, { company_id: company?.id || "", ...extraBody, rows: chunk }, true, { skipErrorAlert: true });
      if (!res?.status) {
        aborted = true; // stop sending further chunks once one fails
        if (!failMessage) {
          failMessage = res?.message || t("import_failed");
          // backend ส่งรายละเอียดมาเป็น ["rows[i].field: msg"] -> แปลง i เป็นเลขแถวในไฟล์ Excel
          if (Array.isArray(res?.errors) && res.errors.length) {
            const lines = res.errors.slice(0, 10).map((line) =>
              String(line).replace(/^rows\[(\d+)\]/, (_, i) => {
                const excelRow = excelRowOf.get(chunk[Number(i)]);
                return excelRow ? `${t("row")} ${excelRow}` : `rows[${i}]`;
              })
            );
            const more = res.errors.length > 10 ? `\n... (+${res.errors.length - 10})` : "";
            failMessage += `\n${lines.join("\n")}${more}`;
          }
        }
        return;
      }
      const d = res.data || {};
      created += d.created ?? 0;
      updated += d.updated ?? 0;
      done = Math.min(done + chunk.length, total);
      this.setState({ import_done: done, import_progress: Math.round((done / total) * 100) });
    };

    // Worker pool: send up to `concurrency` chunks at once for speed. Data sets with
    // intra-file dependencies run one at a time to preserve the row order they need.
    const concurrency = cfg.sequentialImport ? 1 : Math.min(IMPORT_CONCURRENCY, chunks.length || 1);
    let next = 0;
    const worker = async () => {
      while (next < chunks.length && !aborted) {
        const myChunk = chunks[next++];
        await sendChunk(myChunk);
      }
    };
    await Promise.all(Array.from({ length: concurrency }, worker));

    this.setState({ importing: false });

    if (!failMessage) {
      this.resetImport();
      alert(
        "",
        "success",
        t("import_done"),
        `${t("import_created_count")}: ${created}  |  ${t("import_updated_count")}: ${updated}`
      );
    } else {
      // Chunks commit independently -> some rows may already be in. Report the partial
      // result (re-importing is an idempotent upsert by code).
      this.setState({ import_progress: 0, import_done: 0 });
      const partial =
        created + updated > 0
          ? `\n${t("import_created_count")}: ${created}  |  ${t("import_updated_count")}: ${updated}`
          : "";
      alert("", "warning", t("warning"), failMessage + partial);
    }
  };

  // ---- Render --------------------------------------------------------------

  render() {
    const { filter_search, data_sets, import_modal, import_rows, active_key, importing, import_progress, import_done, show_invalid_only, bulk_options, bulk_value } = this.state;

    const keyword = filter_search.trim().toLowerCase();
    const filtered = keyword
      ? data_sets.filter(
          (d) =>
            t(d.name).toLowerCase().includes(keyword) ||
            t(d.description).toLowerCase().includes(keyword) ||
            d.template.toLowerCase().includes(keyword)
        )
      : data_sets;

    const cfg = DATASET_CONFIG[active_key];
    const activeName = data_sets.find((d) => d.key === active_key)?.name || "";

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarWarehouse />

        {/* Hidden file input for import */}
        <input
          ref={this.fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="d-none"
          onChange={this.handleFileSelected}
        />

        <div className="body p-4 mt-3">
          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("import_center")}</h3>
              <div className="text-secondary text-14">{t("import_center_desc")}</div>
            </div>
          </div>

          {/* Search Toolbar */}
          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <label className="fw-bold text-dark text-14 mb-0">{t("search")}:</label>
              <div className="position-relative" style={{ maxWidth: "360px", width: "100%" }}>
                <input
                  type="text"
                  className="form-control wpx-300"
                  style={{ height: "38px" }}
                  placeholder={t("type_to_search")}
                  value={filter_search}
                  onChange={(e) => this.setState({ filter_search: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Data Set Table */}
          <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ minWidth: "900px" }}>
                <thead>
                  <tr className="text-white fw-bold text-12 bg-primary">
                    <th className="py-3 border-0 ps-4">{t("data_set")}</th>
                    <th className="py-3 border-0 text-center">{t("template_file")}</th>
                    <th className="py-3 border-0 text-center pe-4 wpx-450">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center text-secondary py-5">
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((item, idx) => (
                      <tr key={idx} className="border-bottom border-light bg-white">
                        {/* Data Set Info */}
                        <td className="py-3 ps-4">
                          <div className="d-flex align-items-center">
                            <div
                              className={`wpx-35 hpx-35 rounded text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0 fw-bold text-14 bg-${item.color}`}
                            >
                              <span className="icon">{item.icon}</span>
                            </div>
                            <div>
                              <div className="fw-bold text-dark text-15">{t(item.name)}</div>
                              <div className="text-secondary text-12">{t(item.description)}</div>
                            </div>
                          </div>
                        </td>

                        {/* Template File */}
                        <td className="text-center">
                          <span
                            className="badge bg-primary-light text-primary rounded-pill px-3 py-2 fw-bold text-12 pointer"
                            onClick={() => this.handleDownloadTemplate(item)}
                          >
                            {item.template}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="text-center pe-4">
                          <div className="d-flex flex-column align-items-end gap-1">
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleDownloadTemplate(item)}
                              >
                                <span className="icon">{"\uf019"}</span> {t("template")}
                              </button>
                              <button
                                className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleExport(item)}
                              >
                                <span className="icon">{"\uf56e"}</span> {t("export")}
                              </button>
                              {(!IMPORT_PERMISSION[item.key] || can(IMPORT_PERMISSION[item.key])) && (
                              <button
                                className="btn btn-primary px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 text-13 wpx-120"
                                onClick={() => this.handleImport(item)}
                              >
                                <span className="icon">{"\uf56f"}</span> {t("import")}
                              </button>)}
                            </div>
                            <div className="text-success text-11 fw-bold">
                              {DATASET_CONFIG[item.key] ? t("ready_for_import") : t("coming_soon")}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* IMPORT PREVIEW MODAL — คอมโพเนนต์เดียวกับปุ่มนำเข้าในเมนูสต็อกสินค้า */}
        <ImportPreviewModal
          show={import_modal}
          title={t("import_preview_title")}
          subtitle={activeName ? t(activeName) : ""}
          warning={cfg?.confirmDetail ? t(cfg.confirmDetail) : ""}
          columns={cfg ? cfg.columns : []}
          rows={import_rows}
          importing={importing}
          progress={import_progress}
          done={import_done}
          showInvalidOnly={show_invalid_only}
          onToggleInvalidOnly={(value) => this.setState({ show_invalid_only: value })}
          bulkOption={cfg?.bulkOption || null}
          bulkOptions={bulk_options}
          bulkValue={bulk_value}
          onBulkChange={(value) => this.setState({ bulk_value: value })}
          onCancel={this.resetImport}
          onConfirm={this.handleConfirmImport}
        />

      </div>
    );
  }
}
